/**
 * AudioGuide — the public Facade for the realtime AI audio-guide library.
 *
 * This is the single public class that consumer projects interact with.
 * It encapsulates the full set of internal services (IacpClient, GpsTracker,
 * CompassTracker, MapRenderer, TtsPlayer, YoutubePlayer, VoiceRecorder,
 * BuildingResolver, DriftDetector, ReverseGeocoder, WikipediaEnricher,
 * BudgetTimer) behind a stable, opinionated API.
 *
 * Design principle — "Controller Dimension":
 *   Consumers never import individual services. They see only this class
 *   and the typed event surface. The internal service layer is an
 *   implementation detail that can be refactored without breaking
 *   consumer code, as long as the Facade contract stays stable.
 *
 * Typical usage (framework-agnostic):
 *
 *   import { AudioGuide } from '@arkturian/audio-guide';
 *
 *   const guide = new AudioGuide({
 *     session: 'Guide',
 *     cloudApiBootstrap: 'https://cloud-api.arkserver.arkturian.com',
 *     mapboxToken: 'pk...',
 *     defaultMode: 'drift',
 *     defaultTrack: 30,
 *   });
 *
 *   await guide.init();
 *   await guide.mountMap(document.getElementById('map')!);
 *   guide.on('topic:changed', (topic) => renderTopicCard(topic));
 *   guide.on('speech:start', (text) => showSpeechIndicator(text));
 *   guide.start();
 *
 * React consumers should use `@arkturian/audio-guide/react` instead,
 * which wraps this class in idiomatic hooks.
 */
import { EventManager, Logger } from './core';
import { LocationState, TrackingState, BudgetState, DriftContext, AudioQueueState, SongQueueState, TopicCardState, SpecialTrackState, NarratorState, InterestsState, } from './models';
import { IacpClient, GpsTracker, CompassTracker, ReverseGeocoder, BuildingResolver, RADIUS_PRESETS, WikipediaEnricher, wikiLangForCountry, TtsPlayer, MusicPlayer, VoiceRecorder, DriftDetector, BudgetTimer, RealtimeFunctionRouter, RealtimeVoiceSession, } from './services';
import { LocalStateManager } from './services/LocalStateManager';
import { AudioBusManager } from './services/AudioBusManager';
import { AdventureManager } from './services/AdventureManager';
import { StoryPointsManager } from './services/StoryPointsManager';
export const DEFAULT_PLAYER_BEHAVIOR = {
    layerPauseMs: 4000,
    followupBeatsPending: true,
};
/**
 * Public facade for the entire audio-guide runtime.
 * Instantiated once per page/app. Owns the lifecycle of all internal
 * services and models. Emits a curated set of events on the public
 * event surface.
 */
export class AudioGuide {
    constructor(config) {
        this._mapRenderer = null;
        // Lifecycle flags
        this._initialized = false;
        this._transport = 'scooter';
        this._language = 'de';
        /**
         * Narration persona. Independent of `_specialTrack`. Default
         * 'default' means the City Drift Companion voice — third-person,
         * neutral. Other registered values (e.g. 'tschauko') switch the
         * bot into first-person historical-avatar mode while keeping
         * facts sourced from whatever track is active. The bot's
         * GEMINI.md "PERSONA REGISTRY" section defines the available
         * names. Unknown names fall back to default on the bot side.
         *
         * We omit the [PERSONA:...] GPS tag when narrator === 'default'
         * so older bots that don't know the tag keep working unchanged
         * (the bot's no-tag path = default behaviour anyway).
         */
        this._narrator = 'default';
        this._running = false;
        /**
         * Central audio-engine mode — the ONE invariant "exactly one narration
         * source is active". Replaces the former independent _corpusStaticMode /
         * _realtimeMode booleans whose combinations produced double-audio bugs
         * (corpus player + pool agent, realtime + corpus).
         * - 'normal':       pool-agent send-loop narrates (default)
         * - 'realtime':     WebRTC realtime session narrates (Content #1196)
         * - 'corpusStatic': deterministic narration_point corpus narrates (Mode ②)
         * Every narration source checks this gate; transitions ONLY via
         * setRealtimeMode()/setCorpusStaticMode() (guarded shims).
         */
        this._engineMode = 'normal';
        /** deep_dive depth (Content #1213/#1214): 0 = off (normal walk_companion),
         *  1-3 = number of progressively deeper narration layers of the SAME topic.
         *  When >0, gps_update sends <guide_mode>deep_dive</guide_mode> + <depth>N</depth>. */
        this._deepDiveDepth = 0;
        /** Configurable player behavior (Content #1213/#1214) — read by the tts:end
         *  queue state machine. Defaults = classic behavior. */
        this._playerBehavior = { ...DEFAULT_PLAYER_BEHAVIOR };
        this._realtimeSession = null;
        /** Incremented on stop() — in-flight sends compare against this to discard stale responses */
        this._stopGeneration = 0;
        /** Pre-fetched OSM context — populated async on GPS updates, read sync in _sendAndListen */
        this._osmPrefetch = null;
        this._osmPrefetching = false;
        /**
         * First-fix bootstrap flag (Option D). Stays false until the very
         * first GPS position has been enriched with geocode + OSM places.
         * Acts as gate: start() defers the initial _sendAndListen until
         * _acquireFullLocationContext() flips this flag and emits the
         * 'audio-guide:position-acquired' bus event.
         */
        this._firstFixAcquired = false;
        // Orchestration state
        this._sendInterval = null;
        this._lastSongAt = null;
        this._lastGeocodeTs = 0;
        /**
         * Session-Sticky-Snapshot — tracks last-sent settings to suppress
         * the <session> XML-block on CRUISING updates when nothing changed.
         * Builder regenerates the block only on FIRST_UPDATE (incl. first
         * send of the session) or when any sticky-field differs from this
         * snapshot. Null = never sent yet → first send is a FIRST_UPDATE.
         */
        this._sessionSnapshot = null;
        /**
         * Delta-engine state — track what the bot has already received so we
         * don't re-push unchanged blocks every tick. The bot has conversation
         * memory; resending the same `<location>` and `<heard>` strings on
         * every 60s tick wastes its context window and confuses the persona
         * ("Habe ich das schon erzählt?"). Snapshot = "last sent values".
         *
         * - locationSnapshot: last `<location>` text. Same text → omit tag.
         *   Reset to null on stop() so a fresh start re-emits.
         * - heardSnapshot: set of individual heard items already sent. Diff
         *   against current cache → emit only NEW items in `<heard>`. Items
         *   that drop out of the cache (24h decay, see #808) are pruned from
         *   the snapshot so they can be re-emitted if they reappear.
         */
        this._locationSnapshot = null;
        this._heardSnapshot = new Set();
        this._mapContainerId = 'audio-guide-map';
        /** Async callback to fetch "heard" topics for the current position. Set by consumer. */
        this._heardCallback = null;
        this._heardCache = null;
        /** Tracks what THIS session's bot has delivered, with knowledge_id +
         *  km-at-delivery so the bot can decide hard-vs-soft repeat-blocking
         *  out of distance (per GuideTest's feedback: 11x Rotbuche at
         *  different locations is fine if 1km apart; 200m apart is pile-on).
         *  Capped to MAX_DELIVERED_ENTRIES; FIFO eviction. */
        this._deliveredLog = [];
        /** km_progress at the time of the last live-send call. Used as the
         *  km-at-delivery anchor for the next bot response — guide-api
         *  returns km_progress in the live-send response, we stash it here,
         *  pair with the response when it arrives. null when not on a track. */
        this._lastTrackKm = null;
        /** The phase string ("APPROACHING:Wildererplatzl@50m") returned by
         *  guide-api for the in-flight send, surfaced for debug/log only. */
        this._lastPhase = null;
        /**
         * Async batch-translator. Set by consumer. When language != 'de'
         * and this is set, _handleIacpResponse translates content / vibe /
         * additional_topics.{content,vibe} / topic.subtitle from German
         * into the active language before topic-card + TTS see the
         * payload. The bot is told (via prompt) to always emit DE master;
         * downstream we translate per UI choice. topic.title is NEVER
         * translated — Eigennamen like "Lendkanal" should stay literal.
         */
        this._translatorCallback = null;
        /** Async callback for auto track detection via snap-to-path. Set by consumer. */
        this._trackDetectCallback = null;
        this._trackDetectPending = false;
        this._autoTrackId = null;
        /** Async callback to find a reviewed image for a topic title. Set by consumer. */
        this._findImageCallback = null;
        this._findImageFastCallback = null;
        /** Manual GPS override mode — when true, internal gps:update events are ignored */
        this._manualGpsMode = false;
        // Content Package Queue — atomic visual+audio bundles
        this._activePackage = null;
        this._pendingPackage = null;
        /** followup + filler attached to the currently-active main response */
        this._additionalQueue = [];
        /** followup + filler attached to the pending main (fetched eagerly so
         *  there's no silence when the pending main activates). Moved into
         *  _additionalQueue when the pending main becomes active. */
        this._pendingAdditionals = [];
        /** URL of the user's uploaded photo waiting for the next bot response.
         *  When set, the next iacp:response overrides topic.image with this URL
         *  (and disables Wikipedia/Places/Commons enrichment for that package).
         *  Cleared after one use. */
        this._pendingUserPhotoUrl = null;
        /** Surprise-mode: when active, the user's real GPS is ignored and we
         *  teleport to a random curated location every time the queue empties.
         *  Tour-of-the-world from the bathtub. Enabled when the mode is
         *  'surprise', cleared on any other mode. */
        this._surpriseMode = false;
        /** Multi-select category list from onboarding. The library rotates
         *  through this list one-per-GPS-update so a user who picked
         *  "Geschichte" + "Handwerk" gets stories that alternate categories.
         *  Empty array = no intent override (default bot behavior). */
        this._userIntents = [];
        /** Round-robin pointer into _userIntents; advances on each GPS send. */
        this._userIntentIdx = 0;
        /** User-supplied age (years). Appended as [AGE:N] so the bot can
         *  subtly adapt tone. null = feature off or user opted out. */
        this._userAge = null;
        /** Short stable Client-ID for this browser/tab. Persists across page
         *  reloads via localStorage so the bot can tell "same user came back"
         *  vs "fresh browser/tab" — important because a long-lived agent like
         *  GuideTest may be talking to multiple clients in parallel. Used by
         *  the bot to scope FIRST_UPDATE semantics (new client_id → really
         *  fresh, ignore any prior conversation context from other clients).
         *  Format: 8 base36 chars (~41 bits entropy, collision-safe at the
         *  scale of "a single agent's lifetime"). */
        this._clientId = '';
        /** Pending timer for the small breather between queued TTS packages. */
        this._advanceTimer = null;
        this._ttsFetchAbort = null;
        /** Images already shown this session — don't repeat them */
        this._shownImages = new Set();
        this._logSessionCreated = false;
        /** Callback to fetch a random world city. Set via setSurprisePicker
         *  by the app (calls guide-api /cities/random ~169k cities). When unset,
         *  falls back to continental-bbox random sampling. */
        this._surprisePicker = null;
        /**
         * Prefetch "heard" topics in the background. Uses the consumer-provided
         * callback (set via setHeardCallback). Cached per ~500m grid cell, 5min TTL.
         */
        this._heardCallbackLogged = false;
        this._config = {
            defaultMode: 'surprise',
            defaultTransport: 'walking',
            defaultTrack: null,
            defaultBudgetMin: 0,
            defaultIntervalSec: 60,
            osmContext: false,
            ...config,
        };
        this._osmEnabled = this._config.osmContext ?? false;
        this._osmRadiusOverride = this._config.osmRadiusM ?? null;
        this._clientId = this._loadOrGenerateClientId();
        this._bus = new EventManager();
        this._logger = new Logger(this._bus);
        // Models
        this._location = new LocationState(this._bus);
        this._tracking = new TrackingState(this._bus);
        this._budget = new BudgetState(this._bus);
        this._driftCtx = new DriftContext();
        this._audioQueue = new AudioQueueState(this._bus);
        this._songQueue = new SongQueueState(this._bus);
        this._topicCard = new TopicCardState(this._bus);
        this._specialTrack = new SpecialTrackState(this._bus);
        this._narratorState = new NarratorState(this._bus);
        this._interestsState = new InterestsState(this._bus);
        // Services
        this._iacp = new IacpClient(this._config, this._bus);
        if (this._config.guideApiBase) {
            this._iacp.setEnricherBase(this._config.guideApiBase);
            if (this._config.fastFirstTurn)
                this._iacp.setFastFirstTurn(true);
        }
        this._gps = new GpsTracker(this._bus);
        this._compass = new CompassTracker(this._bus);
        this._geocoder = new ReverseGeocoder();
        this._buildingResolver = new BuildingResolver();
        this._wikipedia = new WikipediaEnricher();
        this._musicPlayer = new MusicPlayer(this._config, this._bus, this._songQueue);
        this._ttsPlayer = new TtsPlayer(this._config, this._bus, this._audioQueue, this._musicPlayer);
        this._voice = new VoiceRecorder(this._config, this._bus);
        this._drift = new DriftDetector(this._driftCtx);
        // Realtime function-call router (Content #1196): maps OpenAI-Realtime
        // function-calls onto the existing UI internals. Display-Hints drive the
        // SAME paths the live/corpus delivery uses (topic-card, #1173 gallery,
        // KnowledgeView, map:focus) — no UI rewrite. Never speaks (realtime audio
        // is the WebRTC stream). The adapter keeps the router decoupled from these
        // internals so it stays unit-testable without a browser.
        this._realtimeRouter = new RealtimeFunctionRouter({
            showTopic: (t) => this._topicCard.setTopic({ title: t.title, lat: t.lat, lon: t.lon }),
            showImages: (urls) => this.updateNarrationImages(urls, 'approved'),
            showKnowledgePin: (kid) => this._topicCard.mergeEnrichment({ knowledge_id: kid }),
            focusMap: (m) => this._bus.emit('map:focus', { lat: m.lat, lon: m.lon }),
            setSearching: (on) => this._topicCard.setEnriching(on),
            mediaUrlForStorageId: (id) => `https://api-storage.arkturian.com/storage/media/${id}`,
        });
        this._budgetTimer = new BudgetTimer(this._bus, this._budget);
        // v0.2.0 managers
        this._localState = new LocalStateManager();
        this._audioBus = new AudioBusManager(this._bus);
        this._adventureManager = new AdventureManager(this._bus, this._localState, this._audioBus);
        this._storyPointsManager = new StoryPointsManager(this._bus, this._localState, this._audioBus);
        // Apply defaults
        if (this._config.defaultMode) {
            this._tracking.setMode(this._config.defaultMode);
        }
        if (this._config.defaultTransport) {
            this._transport = this._config.defaultTransport;
        }
        // Auto-detect language from browser
        const browserLang = (navigator.language || 'de').split('-')[0].toLowerCase();
        if (['de', 'en', 'it', 'es', 'fr', 'sl'].includes(browserLang)) {
            this._language = browserLang;
        }
        if (this._config.defaultTrack !== undefined && this._config.defaultTrack !== null) {
            this._specialTrack.setById(this._config.defaultTrack);
            this._remoteLog(`[INIT] defaultTrack=${this._config.defaultTrack} → track active`, 'track');
        }
        else {
            this._remoteLog(`[INIT] defaultTrack=${this._config.defaultTrack} → no track (generic mode)`, 'track');
        }
        if (this._config.defaultBudgetMin !== undefined && this._config.defaultBudgetMin > 0) {
            this._budget.setBudgetMin(this._config.defaultBudgetMin);
        }
        if (this._config.defaultIntervalSec !== undefined) {
            this._tracking.setIntervalSec(this._config.defaultIntervalSec);
        }
    }
    // ──────────────────────────────────────────────────────────────
    // Lifecycle
    // ──────────────────────────────────────────────────────────────
    /** Resolve the IACP server via federation discovery. Call once before start(). */
    async init() {
        if (this._initialized)
            return;
        await this._iacp.findServer();
        this._logger.info(`Server resolved: ${this._iacp.apiBase || '(same-origin)'}`);
        this._logger.info('MusicPlayer ready (AudioContext set on primeAudio)');
        this._wireOrchestration();
        this._initRemoteLog();
        this._initialized = true;
        this._bus.emit('audio-guide:ready');
        this._logger.info(`AudioGuide ready — session: ${this._config.session}`);
    }
    /** Start GPS tracking, compass updates, budget timer, and send-interval. */
    start() {
        if (!this._initialized) {
            throw new Error('AudioGuide: call init() before start()');
        }
        if (this._running)
            return;
        this._tracking.start();
        this._driftCtx.reset();
        this._lastSongAt = null;
        // Only start the internal navigator.geolocation watcher if the
        // consumer didn't opt into external GPS. When gpsSource is 'external',
        // the consumer pushes positions via guide.updateLocation(pos) and
        // we skip navigator.geolocation entirely (to avoid double GPS drain
        // when the host app already has its own location service).
        if (this._config.gpsSource !== 'external') {
            this._gps.start();
            this._compass.start();
        }
        this._budgetTimer.start();
        this._ttsPlayer.primeAudio();
        // Initial send — D9 Bootstrap-Acquire pattern.
        // Wait for the first GPS fix to be fully enriched (city/region/
        // places) before firing the FIRST_UPDATE. The 'position-acquired'
        // event is emitted by _acquireFullLocationContext() once both
        // pipelines have settled (or hit their 3s timeouts).
        //
        // D9.2: NO global failsafe timer here — that races against slow
        // GPS lock (typical 3-8s indoor on iOS). If start()-timer fires
        // before acquire completes, _sendAndListen runs with empty
        // street/city/region. Without a position-acquired listener
        // re-fire, the bus.once never triggers again either.
        //
        // The failsafe lives inside _acquireFullLocationContext (3s per
        // pipeline) so a slow geocoder/OSM call still progresses; if no
        // GPS lock at all comes, _sendAndListen would skip anyway (it
        // checks hasPosition first).
        if (this._tracking.currentMode !== 'surprise') {
            this._bus.once('audio-guide:position-acquired', () => {
                void this._sendAndListen(false);
            });
        }
        const intervalMs = this._tracking.intervalSec * 1000;
        this._sendInterval = window.setInterval(() => {
            // Surprise mode: rhythm is "queue empty → next random location",
            // not wall-clock. The periodic timer would just re-send the same
            // surprise location, wasting tokens.
            if (this._surpriseMode)
                return;
            void this._sendAndListen(false);
        }, intervalMs);
        this._running = true;
        const startMsg = `Tracking started — transport=${this._transport} theme=${this._tracking.currentMode} interval=${this._tracking.intervalSec}s`;
        this._logger.info(startMsg);
        this._remoteLog(startMsg, 'lifecycle');
        // Surprise-mode bootstrap: don't wait for a real GPS fix, jump
        // straight to a random world location.
        if (this._tracking.currentMode === 'surprise') {
            this._surpriseMode = true;
            void this._pickSurpriseLocation();
        }
    }
    /**
     * Unlock the audio channel for TTS playback on mobile browsers.
     *
     * Mobile Safari (and most mobile browsers) require an HTMLAudioElement to
     * be created and played inside a **user gesture** (click / tap / pointerup)
     * before it can play programmatic audio later. `start()` calls this
     * internally, but when `start()` is triggered from a useEffect or other
     * non-gesture context, the unlock silently fails and TTS stays muted.
     *
     * **Call this once from a button handler** (onClick / onPointerUp) before
     * or instead of relying on `start()` to do it:
     *
     * ```tsx
     * <button onPointerUp={() => { guide.primeAudio(); guide.start(); }}>
     *   Start Guide
     * </button>
     * ```
     */
    primeAudio() {
        this._ttsPlayer.primeAudio();
        // Seed the default ambient playlist and start pre-fetching
        this._musicPlayer.seedDefaultPlaylist();
    }
    /**
     * Return a diagnostic snapshot of the library's internal state.
     * Useful for debugging integration issues in consumer apps.
     *
     * ```tsx
     * <pre>{JSON.stringify(guide.debug(), null, 2)}</pre>
     * ```
     */
    debug() {
        const loc = this._location.snapshot();
        return {
            initialized: this._initialized,
            running: this._running,
            session: this._config.session,
            gpsSource: this._config.gpsSource,
            iacpResolved: this._iacp.isResolved,
            iacpApiBase: this._iacp.apiBase,
            hasPosition: this._location.hasPosition,
            position: loc.lat !== null ? { lat: loc.lat, lon: loc.lon, accuracy: loc.accuracy } : null,
            transport: this._transport,
            mode: this._tracking.currentMode,
            sentCount: this._tracking.sentCount,
            intervalSec: this._tracking.intervalSec,
            trackId: this._specialTrack.isActive ? this._specialTrack.trackId : null,
            osmEnabled: this._osmEnabled,
            osmRadius: this.osmRadius,
        };
    }
    /**
     * Push a GPS position from an external source (e.g. the host app's
     * own LocationService). Only meaningful when config.gpsSource === 'external'.
     * The position flows through the same LocationState / bus / orchestration
     * pipeline as internal navigator.geolocation updates would.
     */
    updateLocation(pos) {
        if (this._config.gpsSource !== 'external') {
            this._logger.warn('AudioGuide.updateLocation() called but gpsSource is not "external" — ignoring');
            return;
        }
        this._setLocation(pos);
    }
    /**
     * Manually override the GPS position regardless of gpsSource setting.
     * Use case: long-press on map to teleport the Guide to a chosen location.
     * Triggers a fresh _sendAndListen with the new coordinates.
     */
    setManualLocation(pos) {
        this._manualGpsMode = true; // Lock out internal GPS updates
        this._logger.info(`Manual GPS: ${pos.lat.toFixed(5)}, ${pos.lon.toFixed(5)}`);
        this._remoteLog(`Manual GPS set: ${pos.lat.toFixed(5)}, ${pos.lon.toFixed(5)}`, 'gps');
        // First-fix not done yet: just push the position. start()'s
        // bus.once('audio-guide:position-acquired') listener is still armed
        // and will fire the deferred FIRST_UPDATE once acquire settles.
        if (!this._firstFixAcquired) {
            this._setLocation(pos);
            return;
        }
        // Not running: just record the position, no send.
        if (!this._running) {
            this._setLocation(pos);
            return;
        }
        // Post-first-fix teleport (e.g. long-press on map to far-away
        // location): re-run Bootstrap-Acquire so the new <street>/<city>/
        // <places> are populated before _sendAndListen. Without this,
        // the legacy 500ms setTimeout races against the geocoder + osm
        // prefetch for the new location — same Strandbad-Loretto bug,
        // teleport edition.
        this._firstFixAcquired = false;
        let sendFired = false;
        const fireSend = () => {
            if (sendFired)
                return;
            sendFired = true;
            void this._sendAndListen(false);
        };
        this._bus.once('audio-guide:position-acquired', fireSend);
        window.setTimeout(fireSend, 8000); // failsafe — same as start()
        this._setLocation(pos);
    }
    /** Release manual GPS mode — return to live navigator.geolocation updates */
    releaseManualLocation() {
        this._manualGpsMode = false;
        this._remoteLog('Manual GPS released — live GPS resumed', 'gps');
    }
    get isManualGps() {
        return this._manualGpsMode;
    }
    _setLocation(pos) {
        this._location.updatePosition(pos);
        if (this._mapRenderer) {
            this._mapRenderer.updatePosition(pos.lat, pos.lon, this._location.effectiveHeading);
        }
        // Track auto-detect runs on EVERY position update path — real
        // watchPosition, external push (updateLocation), manual teleport
        // (setManualLocation). Pre-fix it was only wired into the
        // gps:update bus handler, so a teleport from Klagenfurt into
        // Tscheppaschlucht bypassed it entirely and the bot got no
        // <artrack id> tag → blind to track context. The callback has
        // its own _trackDetectPending guard so concurrent calls are
        // safe; bus handler's explicit call is now redundant but kept
        // for callers that emit gps:update without going through
        // _setLocation (legacy path).
        this._detectNearbyTrack(pos.lat, pos.lon);
        // D9 Bootstrap-Acquire: first GPS fix gets a synchronous enrichment
        // pass (geocode + OSM prefetch with timeouts) so the FIRST_UPDATE
        // ships with street/city/region + <places> populated. Subsequent
        // fixes keep the fire-and-forget pattern because the geocoder +
        // OSM cache are already warm.
        if (!this._firstFixAcquired) {
            void this._acquireFullLocationContext(pos.lat, pos.lon);
            return;
        }
        this._throttledReverseGeocode(pos.lat, pos.lon);
        this._prefetchOsmContext(pos.lat, pos.lon);
    }
    /**
     * D9 Bootstrap-Acquire — synchronous first-fix context enrichment.
     *
     * Runs geocode + OSM-prefetch in parallel with 3s timeouts each, then
     * flips _firstFixAcquired and emits 'audio-guide:position-acquired'.
     * start() listens for that event to trigger the deferred FIRST_UPDATE.
     *
     * Without this, fire-and-forget prefetches lose the race against
     * start()'s legacy setTimeout(3000): _osmPrefetch is still null when
     * _sendAndListen fires → the bot never sees nearby POIs (Strandbad
     * Loretto bug, real-device 2026-06-07).
     */
    async _acquireFullLocationContext(lat, lon) {
        const t0 = Date.now();
        await Promise.allSettled([
            // Phase 2a — reverse geocode
            Promise.race([
                this._geocoder.lookup(lat, lon).then((addr) => {
                    if (addr) {
                        this._location.updateAddress(addr.street, addr.city, addr.suburb, addr.region, addr.country, addr.countryCode ?? null);
                    }
                }),
                new Promise((resolve) => setTimeout(resolve, 3000)),
            ]),
            // Phase 2b — OSM places prefetch (only if enabled)
            this._osmEnabled
                ? Promise.race([
                    this._waitForOsmPrefetch(lat, lon),
                    new Promise((resolve) => setTimeout(resolve, 3000)),
                ])
                : Promise.resolve(),
            // Phase 2c — track auto-detect (Fix B 2026-06-09): wait synchronously
            // so the FIRST_UPDATE message already carries <artrack id> when the
            // user starts (or teleports) inside a known track. Pre-fix the
            // detect was fire-and-forget, so a Klagenfurt→Tscheppaschlucht
            // teleport sent the first GPS message before the artrack lookup
            // returned → bot got no track hint → mis-identified Teufelsbrücke
            // as the Ferlach Drau-brücke. Real-device 2026-06-09. 2s timeout
            // is generous; artrack /tracks/nearby returns in <200ms locally.
            Promise.race([
                this._detectNearbyTrack(lat, lon),
                new Promise((resolve) => setTimeout(resolve, 2000)),
            ]),
        ]);
        const dur = Date.now() - t0;
        this._firstFixAcquired = true;
        const snap = this._location.snapshot();
        const placesReady = !!this._osmPrefetch && this._osmPrefetch.tag.includes('[PLACES:');
        const trackTag = this._specialTrack.isActive
            ? `track=${this._specialTrack.trackId}`
            : 'track=none';
        this._remoteLog(`[BOOTSTRAP] first-fix acquired in ${dur}ms — ` +
            `city=${snap.city} region=${snap.region} places=${placesReady ? 'YES' : 'NO'} ${trackTag}`, 'lifecycle');
        this._bus.emit('audio-guide:position-acquired', {
            lat,
            lon,
            durationMs: dur,
            hasCity: !!snap.city,
            hasPlaces: placesReady,
        });
    }
    /**
     * Kick off an OSM prefetch (if not already in-flight) and poll
     * _osmPrefetch until it's populated FOR THE REQUESTED CELL.
     * Resolves as soon as the prefetch lands or when the caller's
     * outer timeout fires (via Promise.race).
     *
     * Critical: cell-match check, NOT just !null. On teleport from
     * Berlin → Lorettoweg the stale Berlin _osmPrefetch is non-null
     * but holds wrong data — a plain !null check would resolve
     * immediately with Berlin's POIs. Match on lat/lon proximity
     * (~110m grid, matches _prefetchOsmContext's sameCell threshold).
     */
    async _waitForOsmPrefetch(lat, lon) {
        this._prefetchOsmContext(lat, lon);
        // Poll in 100ms ticks. Caller wraps this in Promise.race(timeout),
        // so unbounded loop is fine — outer race breaks us out.
        while (true) {
            const p = this._osmPrefetch;
            if (p && Math.abs(p.lat - lat) < 0.001 && Math.abs(p.lon - lon) < 0.001) {
                return;
            }
            await new Promise((r) => setTimeout(r, 100));
        }
    }
    /**
     * Push a compass heading from an external source. Companion to
     * updateLocation() for hosts that also manage their own compass.
     */
    updateCompass(headingDeg) {
        if (this._config.gpsSource !== 'external') {
            this._logger.warn('AudioGuide.updateCompass() called but gpsSource is not "external" — ignoring');
            return;
        }
        this._location.updateCompass(headingDeg);
    }
    /** Stop GPS tracking, cancel send-interval, pause audio. */
    stop() {
        // Allow stop from both running AND paused state
        if (this._sendInterval !== null) {
            window.clearInterval(this._sendInterval);
            this._sendInterval = null;
        }
        // Signal in-flight _sendAndListen calls to discard their response.
        // Without this, a fetch that was already in-flight when stop() was
        // called will still trigger _handleIacpResponse → TTS playback,
        // even in a background tab (Safari keeps Promises alive).
        this._stopGeneration++;
        if (this._ttsFetchAbort) {
            this._ttsFetchAbort.abort();
            this._ttsFetchAbort = null;
        }
        this._activePackage = null;
        this._pendingPackage = null;
        this._additionalQueue = [];
        this._pendingAdditionals = [];
        if (this._advanceTimer !== null) {
            window.clearTimeout(this._advanceTimer);
            this._advanceTimer = null;
        }
        if (this._mapRenderer)
            this._mapRenderer.renderUpcomingTopics([]);
        this._shownImages.clear();
        this._manualGpsMode = false;
        this._gps.stop();
        this._compass.stop();
        this._budgetTimer.stop();
        this._ttsPlayer.stop();
        this._musicPlayer.pause();
        this._tracking.stop();
        this._driftCtx.reset();
        this._osmPrefetch = null;
        this._firstFixAcquired = false;
        // Reset delta snapshots so the next start() resends full state.
        // Without this, a Stop → Start cycle within the same page load
        // would suppress location/heard tags even though a fresh bot
        // session needs them (bot has empty conversation context).
        this._sessionSnapshot = null;
        this._locationSnapshot = null;
        this._heardSnapshot = new Set();
        this._running = false;
        this._logger.info('Tracking stopped');
        this._remoteLog('Tracking stopped', 'lifecycle');
    }
    /**
     * Pause tracking — stops GPS interval + TTS + music but keeps the
     * session alive. Resume with resume(). Unlike stop(), this does NOT
     * reset drift context or signal in-flight requests to discard.
     */
    pause() {
        if (!this._running)
            return;
        if (this._sendInterval !== null) {
            window.clearInterval(this._sendInterval);
            this._sendInterval = null;
        }
        this._ttsPlayer.pause();
        this._musicPlayer.pause();
        this._pendingPackage = null; // Drop pending, keep active (visuals stay)
        this._additionalQueue = [];
        this._pendingAdditionals = [];
        if (this._advanceTimer !== null) {
            window.clearTimeout(this._advanceTimer);
            this._advanceTimer = null;
        }
        this._running = false;
        this._logger.info('Tracking paused');
        this._remoteLog('Tracking paused', 'lifecycle');
        // Emit isRunning=false so the skin knows we're paused (not running).
        // We don't call _tracking.stop() because pause is resumable.
        this._bus.emit('tracking:changed', { ...this._tracking.snapshot(), isRunning: false });
    }
    /**
     * Resume from pause — restarts GPS interval + music.
     * Does NOT re-initialize or re-acquire — just continues where we left off.
     */
    resume() {
        if (this._running)
            return;
        this._running = true;
        this._tracking.start();
        this._musicPlayer.resume();
        // Send GPS update immediately on resume (don't wait for interval)
        window.setTimeout(() => void this._sendAndListen(false), 1000);
        // Restart GPS send interval
        const intervalMs = this._tracking.intervalSec * 1000;
        this._sendInterval = window.setInterval(() => void this._sendAndListen(false), intervalMs);
        this._logger.info('Tracking resumed');
        this._remoteLog('Tracking resumed', 'lifecycle');
        this._bus.emit('tracking:changed', this._tracking.snapshot());
    }
    /** Release all resources. Call on page unload. */
    dispose() {
        this.stop();
        if (this._mapRenderer) {
            this._mapRenderer.dispose();
            this._mapRenderer = null;
        }
        this._musicPlayer.dispose();
        this._bus.removeAllListeners();
        this._initialized = false;
    }
    // ──────────────────────────────────────────────────────────────
    // Configuration (runtime-mutable)
    // ──────────────────────────────────────────────────────────────
    /** Change the content theme (culture / geology / orientation / night / surprise). */
    setMode(mode) {
        this._tracking.setMode(mode);
        this._bus.emit('audio-guide:mode-changed', { mode });
        // Surprise mode: ignore real GPS, teleport to random curated locations
        // around the world. Bathtub-tour-of-the-planet.
        const enableSurprise = mode === 'surprise';
        if (enableSurprise && !this._surpriseMode) {
            this._surpriseMode = true;
            this._remoteLog('Surprise mode ON — random world locations', 'surprise');
            if (this._running)
                void this._pickSurpriseLocation();
        }
        else if (!enableSurprise && this._surpriseMode) {
            this._surpriseMode = false;
            this.releaseManualLocation();
            this._remoteLog('Surprise mode OFF — back to real GPS', 'surprise');
        }
    }
    /** Provide a callback that picks the next random surprise location.
     *  Typically wired to guide-api's /cities/random endpoint. */
    setSurprisePicker(cb) {
        this._surprisePicker = cb;
    }
    /** Persistent per-browser client-id used to disambiguate concurrent
     *  conversations on a shared agent (e.g. GuideTest serving multiple
     *  tabs). Stored in localStorage; survives reload but rotates if the
     *  user clears site data. Exposed read-only so debug overlays can
     *  display it. */
    get clientId() {
        return this._clientId;
    }
    /** Load existing Client-ID from localStorage or mint a fresh one.
     *  Defensive: any localStorage access can throw on private windows,
     *  Safari ITP, or quota errors — every path falls back to a fresh
     *  in-memory ID so the lib never blocks boot. */
    _loadOrGenerateClientId() {
        const KEY = 'audio-guide-client-id';
        try {
            const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(KEY) : null;
            if (stored && /^[a-z0-9]{6,16}$/.test(stored))
                return stored;
        }
        catch { /* fall through */ }
        // 8 base36 chars = ~41 bits entropy. Math.random isn't cryptographically
        // secure but collision risk for "one user, one agent" is negligible.
        const fresh = Math.random().toString(36).slice(2, 10).padEnd(8, '0');
        try {
            if (typeof localStorage !== 'undefined')
                localStorage.setItem(KEY, fresh);
        }
        catch { /* ignore */ }
        return fresh;
    }
    /** Set the user-supplied category list (from onboarding chip-cloud).
     *  The library rotates through these one-per-GPS-update via [INTENT:…]
     *  on the outgoing message — e.g. ["Geschichte","Handwerk"] alternates
     *  history/craftsmanship stories at successive locations. */
    setUserIntents(intents) {
        this._userIntents = intents.map(s => s.trim()).filter(Boolean);
        this._userIntentIdx = 0;
        this._remoteLog(`User intents (${this._userIntents.length}): ${this._userIntents.join(' | ')}`, 'onboard');
    }
    /** Returns the [INTENT:...] tag for the current GPS send and advances
     *  the round-robin pointer. Empty string when no intents configured. */
    _currentIntentTag() {
        if (this._userIntents.length === 0)
            return '';
        const idx = this._userIntentIdx % this._userIntents.length;
        const intent = this._userIntents[idx];
        this._userIntentIdx = (this._userIntentIdx + 1) % Math.max(this._userIntents.length, 1);
        return ` [INTENT:${intent}]`;
    }
    /** Persist the user's age so it's appended to every GPS message.
     *  Pass null to clear. Bot uses it to adapt tone subtly — not to
     *  categorize ("für deine Generation…" is explicitly banned in the
     *  prompt). */
    setUserAge(age) {
        if (age !== null && (!Number.isFinite(age) || age < 1 || age > 120)) {
            this._userAge = null;
            return;
        }
        this._userAge = age;
        this._remoteLog(`User age: ${age ?? 'unset'}`, 'onboard');
    }
    /** Pick a random GPS point via the configured picker callback, with
     *  continental-bbox fallback if the API is unreachable. */
    async _pickSurpriseLocation() {
        // Try the configured picker first (typically /cities/random from guide-api)
        if (this._surprisePicker) {
            try {
                const city = await this._surprisePicker();
                if (city && typeof city.lat === 'number' && typeof city.lon === 'number') {
                    this._remoteLog(`🎲 Surprise: → ${city.name} (${city.lat.toFixed(3)}, ${city.lon.toFixed(3)})`, 'surprise');
                    this.setManualLocation({
                        lat: city.lat, lon: city.lon, accuracy: 50,
                        speed: null, heading: null, timestamp: Date.now(),
                    });
                    return;
                }
            }
            catch (err) {
                this._remoteLog(`🎲 Surprise picker error, falling back to bbox: ${err}`, 'surprise', 'Warning');
            }
        }
        // Fallback: random within continental bounding boxes
        const boxes = [
            { region: 'Europa', minLat: 36, maxLat: 70, minLon: -10, maxLon: 40 },
            { region: 'Asien', minLat: 5, maxLat: 65, minLon: 60, maxLon: 140 },
            { region: 'Afrika', minLat: -35, maxLat: 35, minLon: -18, maxLon: 50 },
            { region: 'Nordamerika', minLat: 25, maxLat: 65, minLon: -130, maxLon: -65 },
            { region: 'Südamerika', minLat: -55, maxLat: 10, minLon: -80, maxLon: -35 },
            { region: 'Ozeanien', minLat: -45, maxLat: -10, minLon: 110, maxLon: 180 },
        ];
        const box = boxes[Math.floor(Math.random() * boxes.length)];
        const lat = box.minLat + Math.random() * (box.maxLat - box.minLat);
        const lon = box.minLon + Math.random() * (box.maxLon - box.minLon);
        this._remoteLog(`🎲 Surprise bbox-fallback: → ${box.region} (${lat.toFixed(3)}, ${lon.toFixed(3)})`, 'surprise');
        this.setManualLocation({
            lat, lon, accuracy: 50,
            speed: null, heading: null, timestamp: Date.now(),
        });
    }
    /** Change the transport mode (walking / bike / scooter / car). */
    setTransport(transport) {
        this._transport = transport;
        this._bus.emit('audio-guide:transport-changed', { transport });
    }
    /**
     * Set the deep_dive depth (Content #1213/#1214). 0 = off; 1-3 = how many
     * progressively deeper layers of the SAME topic the agent returns as
     * additional_topics (content=L1, additional_topics[0]=L2, [1]=L3). Additive +
     * inert: until the guide.md persona supports deep_dive the agent ignores the
     * tags, so sending them breaks nothing.
     */
    setDeepDiveDepth(depth) {
        this._deepDiveDepth = Math.max(0, Math.min(3, Math.floor(depth || 0)));
        // The <session> block (which carries guide_mode/depth) is sticky — only sent
        // when changed. Invalidate the snapshot so the new depth re-sends next tick,
        // even on a mid-conversation change (window.__setDeepDive / UI toggle).
        this._sessionSnapshot = null;
    }
    /** Current deep_dive depth (0 = off). */
    get deepDiveDepth() {
        return this._deepDiveDepth;
    }
    /**
     * Configure the narration-player behavior (Content #1213/#1214). Merges into the
     * current config; the tts:end state machine reads it live. One knob for the whole
     * player — pauses (`layerPauseMs`), follow-on rules (`followupBeatsPending`). No
     * per-mode branches. e.g. deep_dive preset: `{layerPauseMs: 8000, followupBeatsPending: false}`.
     */
    setPlayerBehavior(behavior) {
        this._playerBehavior = { ...this._playerBehavior, ...behavior };
    }
    /** Current player behavior config. */
    get playerBehavior() {
        return { ...this._playerBehavior };
    }
    /** Set the language for Guide responses and TTS. */
    setLanguage(lang) {
        this._language = lang;
        this._logger.info(`Language set: ${lang}`);
        this._remoteLog(`Language: ${lang}`, 'settings');
    }
    get currentLanguage() {
        return this._language;
    }
    /**
     * Set the narration persona — controls voice/style independently
     * of which track is active. 'default' = City Drift Companion (no
     * GPS tag emitted, backwards-compatible). Other registered names
     * like 'tschauko' switch the bot into first-person mode. See
     * GEMINI.md "PERSONA REGISTRY" on the bot side for the full list.
     *
     * Delegates to NarratorState which owns persistence + URL bootstrap.
     * The internal _narrator string mirror is kept for the
     * personaTag builder in _sendAndListen (cheap read on the hot
     * path); we keep it in sync inside this setter.
     */
    setNarrator(name) {
        const normalized = (name || 'default').trim() || 'default';
        if (this._narrator === normalized)
            return;
        const prev = this._narrator;
        this._narrator = normalized;
        this._narratorState.setById(normalized);
        this._logger.info(`Narrator set: ${prev} → ${normalized}`);
        this._remoteLog(`Narrator: ${prev} → ${normalized}`, 'settings');
        this._bus.emit('audio-guide:narrator-changed', { narrator: normalized });
    }
    get currentNarrator() {
        return this._narrator;
    }
    /**
     * Set the multi-select user interests. Pass the full slug list each
     * time — the state container dedups internally. Emits the
     * [INTERESTS:slug1,slug2,...] GPS tag in subsequent updates UNLESS
     * a SpecialTrack is active, in which case the track's curated
     * dimensions outrank user curiosity (priority enforced in the GPS
     * message builder, not here).
     */
    setInterests(slugs) {
        this._interestsState.setAll(slugs);
        const list = this._interestsState.current;
        this._logger.info(`Interests set: [${list.join(',')}]`);
        this._remoteLog(`Interests: [${list.join(',')}]`, 'settings');
        this._bus.emit('audio-guide:interests-changed', { slugs: list });
    }
    /** Toggle a single interest slug. Convenience for chip-row UIs. */
    toggleInterest(slug) {
        this._interestsState.toggle(slug);
        const list = this._interestsState.current;
        this._logger.info(`Interest toggled → [${list.join(',')}]`);
        this._bus.emit('audio-guide:interests-changed', { slugs: list });
    }
    get currentInterests() {
        return this._interestsState.current;
    }
    /** Activate a curated special-track by id, or null to switch off. */
    /**
     * Route-Planner overlay (optional, toggle-driven): draw a planned route
     * line + its POIs/screen-points on the map. Additive — no effect on the
     * live guide. Delegates to MapRenderer's `plan-*` layer namespace.
     */
    showRouteOverlay(routes, points) {
        this._mapRenderer?.renderRouteOverlay(routes, points);
    }
    hideRouteOverlay() {
        this._mapRenderer?.clearRouteOverlay();
    }
    setTrack(trackId) {
        const wasActive = this._specialTrack.isActive;
        const wasTrackId = this._specialTrack.trackId;
        this._specialTrack.setById(trackId);
        this._bus.emit('audio-guide:track-changed', { trackId });
        this._logger.info(`[TRACK] setTrack(${trackId}) — was=${wasTrackId} isActive=${this._specialTrack.isActive}`);
        this._remoteLog(`[TRACK] setTrack(${trackId}) was=${wasTrackId} active=${this._specialTrack.isActive}`, 'track');
        // Mode-switches reach the bot through ONE channel only: the next
        // <gps_update>'s <session> block — <artrack id> present = track
        // mode, absent after presence = track left + persona reset
        // (guide.md in-band rule, deployed + bench-verified 2026-06-11).
        // Both legacy [SYSTEM] pings are gone: the bot rejected them per
        // spec and each one burned a full agent turn before the real
        // message (live trace 2026-06-12, msg #3193).
        //
        // Force an immediate GPS-update so the change hits the bot now,
        // not up to 60s later. Skip when no fix yet (start()'s first send
        // carries the track anyway) or when the last send ALREADY carried
        // this trackId (session-snapshot match — a duplicate would just
        // burn a turn).
        if (this._running && this._iacp.isResolved) {
            const lastSentTrackId = this._sessionSnapshot?.trackId ?? null;
            // Boot guard: when track-detect completes BEFORE the first send
            // (D9 bootstrap waits for it), the snapshot is still null and the
            // dedup can't catch the duplicate — but start()'s deferred
            // FIRST_UPDATE already carries the track. Forced send is only
            // for MID-TOUR track changes (live trace 2026-06-12: #3200+#3201
            // in the same second were boot double-sends).
            if (this._firstFixAcquired &&
                this._location.hasPosition &&
                lastSentTrackId !== (trackId ?? null)) {
                void this._sendAndListen(false);
            }
        }
    }
    /** Set the ride-budget in minutes (0 = unlimited). */
    setBudget(minutes) {
        this._budget.setBudgetMin(minutes);
        this._budget.resetStart();
    }
    /** Set the send-interval in seconds. */
    setUpdateInterval(seconds) {
        this._tracking.setIntervalSec(seconds);
        if (this._sendInterval !== null && this._running) {
            window.clearInterval(this._sendInterval);
            this._sendInterval = window.setInterval(() => void this._sendAndListen(false), seconds * 1000);
        }
    }
    /** Enable or disable OSM context enrichment at runtime. */
    setOsmContext(enabled) {
        this._osmEnabled = enabled;
        this._bus.emit('audio-guide:osm-changed', { enabled, radius: this.osmRadius });
    }
    /** Override the OSM radius in meters. Pass null to use the transport-mode preset. */
    setOsmRadius(meters) {
        this._osmRadiusOverride = meters;
        this._bus.emit('audio-guide:osm-changed', { enabled: this._osmEnabled, radius: this.osmRadius });
    }
    /** Current effective OSM radius based on override or transport preset. */
    get osmRadius() {
        if (this._osmRadiusOverride !== null)
            return this._osmRadiusOverride;
        return RADIUS_PRESETS[this._transport] ?? 200;
    }
    /** Whether OSM context enrichment is currently enabled. */
    get osmEnabled() {
        return this._osmEnabled;
    }
    /**
     * Set a callback that returns topics the user already heard near a position.
     * The result is injected as [HEARD:...] into GPS messages so the Guide
     * agent avoids repeating content.
     */
    setHeardCallback(cb) {
        this._heardCallback = cb;
    }
    /**
     * Set the batch-translator callback. When set + active language is
     * not 'de', responses are translated before they reach the
     * topic-card and TTS. The callback receives parallel arrays and
     * must return parallel arrays of the same length.
     *
     * Pass null to disable (e.g. when user switches back to DE).
     */
    setTranslatorCallback(cb) {
        this._translatorCallback = cb;
    }
    /**
     * Set a callback that finds a reviewed/cached image for a topic title.
     * Used in enrichment as PRIORITY 0 — before Wikipedia/Places lookups.
     * Returns null if no good cached image is available.
     */
    setFindImageCallback(cb) {
        this._findImageCallback = cb;
    }
    /**
     * Set a callback for fast live image search (Commons + DuckDuckGo, no AI review).
     * Used as enrichment fallback when Wikipedia + cache miss, before Places.
     * Should return ~3-5s. Returns null if nothing found.
     */
    setFindImageFastCallback(cb) {
        this._findImageFastCallback = cb;
    }
    /**
     * Set a callback that detects nearby artrack tracks via snap-to-path.
     * Returns trackId if user is within threshold, null otherwise.
     * The library auto-activates/deactivates the track based on proximity.
     */
    setTrackDetectCallback(cb) {
        this._trackDetectCallback = cb;
    }
    /**
     * Switch the AI generation path. See ADR-001 (docs/architecture/) for
     * the dual-mode rationale.
     *
     *   'pool'   — cloud-api → AgentOS pool → Codex CLI (default). Stateful
     *              conversation memory inside the tmux session; ~$0 cost
     *              but capped at the pool size (currently 5 slots) and
     *              vulnerable to tmux/CLI crashes.
     *   'direct' — guide-api → OpenAI Chat Completions (gpt-4.1-nano).
     *              Stateless, per-call billing (~0.03¢ per GPS update),
     *              unlimited parallelism within Anthropic/OpenAI rate
     *              limits. Requires `directEndpoint` (URL of the Direct
     *              backend, typically guide-api `/api/v1/ai/generate`).
     *
     * Safe to call at any time. In-flight requests already on the pool
     * path keep going on the pool; new sends use the chosen mode.
     */
    setAiMode(mode, directEndpoint) {
        this._iacp.setAiMode(mode, directEndpoint);
    }
    /** Current AI generation mode. */
    get aiMode() { return this._iacp.aiMode; }
    // ──────────────────────────────────────────────────────────────
    // User Actions
    // ──────────────────────────────────────────────────────────────
    /** "Drift me" — request a spontaneous suggestion from Guide. */
    async requestSuggestion() {
        await this._sendAndListen(true);
    }
    /** Submit recorded audio (from VoiceRecorder or external source) to Guide. */
    async askVoiceQuestion(audio) {
        this._remoteLog(`Voice: blob ${audio.size}B, transcribing…`, 'voice');
        try {
            const transcript = await this._voice.transcribe(audio);
            this._remoteLog(`Voice transcript: "${(transcript || '').slice(0, 80)}${(transcript || '').length > 80 ? '…' : ''}"`, 'voice');
            if (transcript) {
                await this._sendVoiceMessage(transcript);
            }
            else {
                this._remoteLog('Voice: empty transcript, not sending', 'voice', 'Warning');
            }
        }
        catch (err) {
            this._remoteLog(`Voice transcribe error: ${err instanceof Error ? err.message : err}`, 'voice', 'Error');
            this._bus.emit('audio-guide:error', err);
        }
    }
    /**
     * Send a photo to Guide. Uploads the image to cloud-api, then sends
     * a queue message referencing the path. Claude uses its Read tool
     * to view the image and generate a response (content/topic/song).
     *
     * @param photo Photo blob (JPEG/PNG)
     * @param question Optional user question; defaults to "Was siehst du hier?"
     */
    async askPhotoQuestion(photo, question) {
        // Upload to Storage API → get public URL → send URL to agent as text.
        // Agent (Claude Code) reads the image via URL with its Read tool.
        const origSize = photo.size;
        this._logger.info(`📷 askPhotoQuestion: orig ${(origSize / 1024).toFixed(1)}KB type=${photo.type || '?'}`);
        this._remoteLog(`📷 Photo: orig ${(origSize / 1024).toFixed(1)}KB`, 'photo');
        this._bus.emit('photo:uploading');
        try {
            // 1. Client-side compress to Full-HD JPEG (max 1920px on long edge,
            //    quality 0.85). iPhone shots are 3-5 MB raw; this typically drops
            //    them to 300-700 KB so the upload + agent-fetch are snappy and
            //    Storage doesn't store unnecessarily huge masters.
            const photoToUpload = await AudioGuide._compressPhoto(photo, 1920, 0.85)
                .catch(err => {
                this._remoteLog(`📷 compress failed, uploading original: ${err}`, 'photo', 'Warning');
                return photo;
            });
            this._remoteLog(`📷 Photo: compressed ${(photoToUpload.size / 1024).toFixed(1)}KB (-${Math.round((1 - photoToUpload.size / origSize) * 100)}%)`, 'photo');
            const fd = new FormData();
            fd.append('file', photoToUpload, `photo-${Date.now()}.jpg`);
            fd.append('context', 'gps-tracker-photo');
            fd.append('is_public', 'true');
            fd.append('ai_mode', 'none');
            fd.append('ttl_hours', '24');
            const uploadRes = await fetch('https://api-storage.arkturian.com/storage/upload', {
                method: 'POST',
                headers: { 'X-API-KEY': 'Inetpass1' },
                body: fd,
            });
            if (!uploadRes.ok) {
                const errBody = await uploadRes.text().catch(() => '');
                throw new Error(`Storage upload HTTP ${uploadRes.status}: ${errBody.slice(0, 200)}`);
            }
            const uploadData = await uploadRes.json();
            if (!uploadData.id)
                throw new Error(`Storage upload returned no id: ${JSON.stringify(uploadData).slice(0, 200)}`);
            // Use the clean /storage/media/{id} URL — no query params that could break embedding
            const imageUrl = `https://api-storage.arkturian.com/storage/media/${uploadData.id}`;
            this._logger.info(`📷 Uploaded: id=${uploadData.id} url=${imageUrl}`);
            this._remoteLog(`📷 Uploaded: id=${uploadData.id}`, 'photo');
            // 2. Send URL to the agent — agent reads the image via URL
            const snap = this._location.snapshot();
            const posStr = snap.lat !== null ? ` | Position: ${snap.lat.toFixed(5)},${snap.lon.toFixed(5)}` : '';
            const ctxTag = this._specialTrack.isActive ? ` [TRACK:${this._specialTrack.trackId}]` : '';
            const userQ = question || 'Was siehst du hier? Erzähl mir etwas dazu.';
            const text = `[PHOTO_FROM_USER] Der User hat ein Foto gemacht. Bild-URL: ${imageUrl} — Bitte das Bild anschauen und darauf basierend antworten. Nutzer-Frage: ${userQ}${posStr} | Transport: ${this._transport} | Theme: ${this._tracking.currentMode}${ctxTag}`;
            this._bus.emit('photo:sent');
            // Stash the user's photo URL — the next iacp:response will use this
            // as the topic-card image instead of whatever the bot returns or the
            // Wikipedia enrichment would pick. The user expects to see their own
            // photo as the cover of the bot's reply.
            this._pendingUserPhotoUrl = imageUrl;
            // Same fix as voice: pause current TTS + clear the playback pipeline
            // so the photo response activates immediately when it arrives.
            // Without this, the photo answer queues behind whatever the user was
            // already listening to and may never play.
            this.pauseSpeech();
            this._activePackage = null;
            this._pendingPackage = null;
            this._additionalQueue = [];
            this._pendingAdditionals = [];
            const msgId = await this._iacp.sendMessage(text, 'gps-tracker');
            this._logger.info(`📷 → Sent msgId=${msgId}`);
            this._remoteLog(`📷 → Sent msgId=${msgId} url=${imageUrl}`, 'photo');
            await this._iacp.pollResponse(msgId);
            this._remoteLog(`📷 ✓ Response received msgId=${msgId}`, 'photo');
        }
        catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            this._logger.error(`📷 Error: ${msg}`);
            this._remoteLog(`📷 ERROR: ${msg}`, 'photo', 'Error');
            this._bus.emit('audio-guide:error', err);
        }
    }
    /** "More like this" — ask Guide for another recommendation in the same vein. */
    async moreLikeThis() {
        if (!this._location.hasPosition)
            return;
        const snap = this._location.snapshot();
        const trackTag = this._specialTrack.isActive
            ? ` [TRACK:${this._specialTrack.trackId}]`
            : '';
        const text = '[MORE LIKE THIS] Mode: ' +
            this._tracking.currentMode +
            ' | Position: ' +
            snap.lat.toFixed(5) +
            ',' +
            snap.lon.toFixed(5) +
            ' — Mehr von der gleichen Sorte bitte.' +
            trackTag;
        try {
            const msgId = await this._iacp.sendMessage(text, 'gps-tracker');
            await this._iacp.pollResponse(msgId);
        }
        catch (err) {
            this._bus.emit('audio-guide:error', err);
        }
    }
    /** Enqueue a pre-fetched MP3 for TTS playback (e.g. warmup cache). */
    speakBuffer(buffer, label) {
        // Track as active package so tts:end handler knows to advance
        this._activePackage = {
            response: { id: 0 },
            ttsBuffer: buffer,
            label: label || 'cached',
        };
        this._ttsPlayer.speakBuffer(buffer, label);
    }
    /** Pause the currently-playing TTS (song continues). */
    pauseNarration() {
        this._ttsPlayer.pause();
    }
    /**
     * Deterministically deliver one pre-built narration_point from the static
     * ArTrack corpus — Mode ② (Statisch/ArTrack). Synthesizes an IacpResponse
     * and routes it through the SAME delivery path as a live bot response
     * (translation, topic-card visuals, TTS queue), so no rendering or audio
     * logic is duplicated — "a second view over the same controller layer".
     * NO agent is involved: this is the corpus-consumption half of the
     * deterministic-delivery architecture (#1118).
     *
     * TTS today: `audioStorageId` is null → the text is TTS'd on playback via
     * the existing fallback. Once the corpus-builder pre-renders TTS and fills
     * `audioStorageId` (a storage-api media id) the pre-rendered audio is
     * preferred automatically — see the audioStorageId branch below.
     *
     * No-op when the guide is not running (start() not called / stopped).
     */
    async deliverNarration(np) {
        if (!this._running) {
            this._logger.warn('deliverNarration skipped — guide is not running');
            return;
        }
        // Engine gate: while the realtime WebRTC session narrates, corpus TTS must
        // not stack on top of it (double audio). Same invariant as _sendAndListen.
        if (this._engineMode === 'realtime') {
            this._logger.warn('deliverNarration skipped — realtime engine is active');
            this._bus.emit('audio-guide:send-skipped', { reason: 'realtime-mode' });
            return;
        }
        const topic = {
            title: np.title || '',
            subtitle: np.subtitle,
            lat: np.lat,
            lon: np.lon,
            knowledge_id: np.knowledgeId ?? null,
        };
        // Pre-resolved images from the narration_point (stage-1 provisional /
        // stage-2 vision-approved, Content #1173): use them as-is + SKIP the
        // client-side enrichment. The server already resolved + vision-reviewed the
        // image — re-resolving client-side is exactly the St-Ruprecht mismatch this fixes.
        let skipImageEnrich = false;
        if (np.images && np.images.length) {
            topic.image = np.images[0];
            // Gallery-Slideshow nur wenn Stufe 2 approved + mehrere Bilder.
            if (np.imageStatus === 'approved' && np.images.length > 1)
                topic.images = np.images;
            skipImageEnrich = true;
        }
        else if (np.imageStatus) {
            // Pipeline lief, aber KEIN Bild aufgelöst (rejected / leeres provisional):
            // der Server hat „kein Bild" entschieden → NICHT client-enrichen, sonst
            // zieht die 4-Stage-Client-Resolution doch noch ein evtl. falsches Bild —
            // genau der St-Ruprecht-Bug. Lieber kein Bild als ein falsches.
            skipImageEnrich = true;
        }
        const res = {
            id: -(np.orderId + 1), // synthetic negative id — never collides with bot msg ids
            content: np.text,
            topic,
        };
        if (skipImageEnrich)
            res.__skipImageEnrichment = true;
        // Translate to the UI language if a translator is wired (title stays literal).
        if (this._translatorCallback && this._language && this._language !== 'de') {
            try {
                await this._translateResponseInPlace(res, this._language);
            }
            catch (err) {
                this._logger.warn(`Narration translate failed (lang=${this._language}), serving DE master: ${err}`);
            }
        }
        // Topic card IMMEDIATELY — the corpus text is already known, so unlike the
        // live path we do NOT block the card on the TTS fetch. (Blocking would also
        // wedge ordered stepping: narrationCorpus' delivering-guard stays set for
        // the whole fetch, so a slow/hung TTS swallows every subsequent point.)
        const pkg = {
            response: res,
            ttsBuffer: null,
            label: res.topic?.title || 'narration',
            kind: 'main',
        };
        this._activePackage = pkg;
        this._applyPackageVisuals(pkg);
        // TTS best-effort + non-blocking. Prefer pre-rendered audio (audioStorageId)
        // once the corpus-builder fills it, else TTS the text on the fly. A newer
        // delivery aborts the previous in-flight fetch (clean ordered stepping).
        if (this._ttsFetchAbort)
            this._ttsFetchAbort.abort();
        this._ttsFetchAbort = new AbortController();
        const signal = this._ttsFetchAbort.signal;
        const text = res.content || '';
        const audio = typeof np.audioStorageId === 'number'
            ? fetch(`https://api-storage.arkturian.com/storage/media/${np.audioStorageId}`, { credentials: 'omit' })
                .then((r) => {
                if (!r.ok)
                    throw new Error(`storage media ${np.audioStorageId} → HTTP ${r.status}`);
                return r.arrayBuffer();
            })
            : text.trim()
                ? this._ttsPlayer.fetchAudio(text, signal)
                : Promise.resolve(null);
        void audio
            .then((buf) => {
            if (buf && !signal.aborted) {
                pkg.ttsBuffer = buf;
                this._ttsPlayer.speakBuffer(buf, pkg.label);
            }
        })
            .catch((err) => {
            if (err?.name !== 'AbortError')
                this._logger.warn(`Narration TTS failed: ${err}`);
        });
    }
    /**
     * Static-corpus mode toggle (Mode ② Statisch/ArTrack). When ON, the agent
     * send-loop is suppressed — the guide plays only the deterministic
     * narration_point corpus. GPS + TTS keep flowing; the live path is
     * untouched and restored when OFF.
     */
    setCorpusStaticMode(on) {
        if (on) {
            // Realtime is an explicit engine swap — the corpus toggle must not steal
            // the engine mid-session (would stack corpus TTS on the WebRTC voice).
            if (this._engineMode === 'realtime') {
                this._logger.warn('Corpus static mode ignored — realtime engine is active');
                return;
            }
            this._setEngineMode('corpusStatic', 'corpus toggle ON');
        }
        else if (this._engineMode === 'corpusStatic') {
            this._setEngineMode('normal', 'corpus toggle OFF');
        }
    }
    /** Current audio-engine mode (exactly one narration source active). */
    get engineMode() {
        return this._engineMode;
    }
    _setEngineMode(mode, reason) {
        if (this._engineMode === mode)
            return;
        const prev = this._engineMode;
        this._engineMode = mode;
        this._logger.info(`Engine mode ${prev} → ${mode} (${reason})`);
        this._bus.emit('audio-guide:engine-mode', { mode, prev, reason });
    }
    /**
     * Realtime live-voice mode (Content #1196). When ON, the deterministic/live
     * send-loop is suppressed — audio + narration come from the realtime WebRTC
     * session; the existing visuals are driven by dispatchRealtimeFunctionCall
     * display-hints. Additive + abschaltbar; the live/corpus paths are untouched
     * and restored when OFF.
     */
    setRealtimeMode(on) {
        if (on) {
            this._setEngineMode('realtime', 'realtime session/engine ON');
        }
        else if (this._engineMode === 'realtime') {
            this._setEngineMode('normal', 'realtime OFF');
        }
    }
    /**
     * Dispatch one OpenAI-Realtime function-call (Content #1196). Display-Hints
     * (`show_topic`/`show_image`/`show_knowledge_pin`/`focus_map`) are applied to
     * the existing UI locally; Read/Persist calls return a backend descriptor for
     * the client transport to POST (stamping `X-Session-ID`). NEVER speaks —
     * realtime audio is the WebRTC stream, not TTS.
     */
    dispatchRealtimeFunctionCall(call) {
        return this._realtimeRouter.route(call);
    }
    /**
     * Start a realtime voice-guide session (Content #1196). Opens the WebRTC pipe
     * (mic + remote model audio) and routes the model's function-calls through
     * dispatchRealtimeFunctionCall. Suppresses the live/corpus send-loop while
     * active. Endpoints default off `guideApiBase`; override via opts. Requires a
     * browser (getUserMedia / RTCPeerConnection) + a live token endpoint.
     */
    async startRealtimeSession(opts = {}) {
        if (this._realtimeSession?.running)
            return;
        // The realtime endpoints live under /api/v1. `this._config.guideApiBase` is
        // the host WITHOUT /api/v1 (used by the live GPS pipeline), so the app must
        // pass the /api/v1 base via opts.guideApiBase. Derive guideApiBase +
        // sessionStartUrl AFTER the spread so opts.guideApiBase actually takes effect.
        const guideApiBase = (opts.guideApiBase || this._config.guideApiBase || '').replace(/\/$/, '');
        const config = {
            aiApiToolBase: 'https://api-ai.arkturian.com/ai/realtime/tool',
            // PTT (Hybrid #1233) must NOT claim the narration engine — the continuous
            // ElevenLabs narration keeps running, the session only answers held-button
            // questions. Explicit opts.suppressNarration still wins (spread below).
            suppressNarration: opts.inputMode !== 'ptt',
            ...opts,
            guideApiBase,
            sessionStartUrl: opts.sessionStartUrl || `${guideApiBase}/realtime/session/start`,
        };
        this._realtimeSession = new RealtimeVoiceSession(config, {
            dispatch: (call) => this._realtimeRouter.route(call),
            setMode: (on) => this.setRealtimeMode(on),
            setSearching: (on) => this._topicCard.setEnriching(on),
            log: (level, msg) => this._logger[level](`[realtime] ${msg}`),
            // Mobile-Autoplay: Lib meldet block/playing, die App rendert den
            // "tap to hear"-Button (Logik Lib, UI App). Über den Event-Bus entkoppelt.
            onAudioBlocked: () => this._bus.emit('audio-guide:realtime-audio-blocked'),
            onAudioPlaying: () => this._bus.emit('audio-guide:realtime-audio-playing'),
            onTranscript: (text, done) => this._bus.emit('audio-guide:realtime-transcript', { text, done }),
            onServerEvent: (type, detail) => this._bus.emit('audio-guide:realtime-server-event', { type, detail }),
            onStopped: (reason) => this._bus.emit('audio-guide:realtime-stopped', { reason }),
        });
        await this._realtimeSession.start();
    }
    /** Stop the active realtime voice session (restores the live/corpus path). */
    async stopRealtimeSession() {
        await this._realtimeSession?.stop();
        this._realtimeSession = null;
    }
    /**
     * Push-to-Talk gate for the realtime session (Hybrid #1233, inputMode:'ptt').
     * Hold → mic attached (audio flows); release → mic detached + turn committed.
     * No-op when no realtime session is running.
     */
    async setRealtimeMicActive(on) {
        await this._realtimeSession?.setMicActive(on);
    }
    /**
     * Re-attempt realtime audio playback inside a user gesture (mobile autoplay
     * recovery). Call from a tap handler after the 'audio-guide:realtime-audio-
     * blocked' event fired. Returns true if playback started.
     */
    async resumeRealtimeAudio() {
        return (await this._realtimeSession?.resumeAudio()) ?? false;
    }
    /**
     * GPS-triggered proactive realtime narration (Content #1196) — builds the
     * location from the current GPS snapshot and asks the realtime session to
     * narrate. This is the realtime parallel of the normal pipeline's
     * _sendAndListen; persist + corpus + UI run through the same shared paths.
     * No-op when no realtime session is running.
     */
    narrateRealtimeLocation() {
        if (!this._realtimeSession?.running)
            return;
        const snap = this._location.snapshot();
        if (typeof snap.lat !== 'number' || typeof snap.lon !== 'number')
            return;
        this._realtimeSession.narrateLocation({ lat: snap.lat, lon: snap.lon, city: snap.city });
    }
    /**
     * Live-Swap (Content #1173): aktualisiert die Bilder der AKTUELL gezeigten
     * Topic-Card OHNE neu zu sprechen — für den provisional→approved Übergang
     * eines narration_point (Stufe-2-Vision-Review ~38s nach Auslieferung).
     * approved → Gallery (>1) / Einzelbild (1); rejected/leer → Bild entfernen
     * (lieber kein Bild als ein falsches — St-Ruprecht-Fix live).
     */
    updateNarrationImages(images, imageStatus) {
        if (!this._topicCard.topic)
            return;
        const patch = {};
        if (imageStatus === 'approved' && images.length) {
            patch.image = images[0];
            patch.images = images.length > 1 ? images : undefined;
        }
        else {
            patch.image = undefined;
            patch.images = undefined;
        }
        this._topicCard.mergeEnrichment(patch);
    }
    /** Skip the currently playing song; the next queued one starts. */
    skipCurrentSong() {
        this._musicPlayer.skip();
    }
    /**
     * Kill the Guide session on the cloud-api and spawn a fresh one.
     * Useful when Guide is stuck in a loop or when starting a new day.
     */
    async resetSession(model = 'sonnet') {
        const session = this._config.session;
        const base = this._iacp.apiBase || this._config.cloudApiBootstrap;
        this._driftCtx.reset();
        this._lastSongAt = null;
        this._ttsPlayer.stop();
        this._logger.info(`Rebooting Guide session (model=${model})...`);
        try {
            await fetch(`${base}/api/sessions/${encodeURIComponent(session)}`, {
                method: 'DELETE',
            });
            await new Promise((resolve) => setTimeout(resolve, 1500));
            await fetch(`${base}/api/sessions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: session,
                    pretty: true,
                    model,
                    agent: 'claude',
                }),
            });
            this._logger.info(`Guide session rebooted as ${model}`);
        }
        catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            this._logger.error(`Reboot failed: ${msg}`);
            this._bus.emit('audio-guide:error', err);
        }
    }
    // ──────────────────────────────────────────────────────────────
    // Map Mounting
    // ──────────────────────────────────────────────────────────────
    /**
     * Mount the Mapbox map into a container element. The library handles
     * the Tokyo-Night map style, user position marker, trail line, POI
     * markers, destination pin, and the pulsing narrative-focus marker.
     *
     * Consumers pass their own container — it can be styled however they want.
     */
    async mountMap(container) {
        if (this._mapRenderer) {
            this._logger.warn('Map already mounted; unmount first.');
            return;
        }
        if (!this._config.mapboxToken) {
            throw new Error('AudioGuide.mountMap(): no mapboxToken in config. Either provide a token to ' +
                'use the built-in Mapbox map, or skip mountMap() entirely and use your own ' +
                'map implementation (the library works headless without a map).');
        }
        // Ensure the container has an id so MapRenderer can find it via querySelector
        if (!container.id) {
            container.id = this._mapContainerId;
        }
        // Lazy: mapbox-gl (~1MB) only loads when a map is actually mounted — the
        // static import chain used to drag it into the boot bundle of every app.
        const { MapRenderer } = await import('./services/MapRenderer.js');
        this._mapRenderer = new MapRenderer(this._config.mapboxToken, container.id, this._bus);
        await this._mapRenderer.initialize();
        this._bus.on('location:changed', (pos) => {
            if (pos.lat !== null && pos.lon !== null) {
                this._mapRenderer.updatePosition(pos.lat, pos.lon, pos.effectiveHeading);
            }
        });
    }
    /** Unmount and dispose the map. */
    unmountMap() {
        if (this._mapRenderer) {
            this._mapRenderer.dispose();
            this._mapRenderer = null;
        }
    }
    // ──────────────────────────────────────────────────────────────
    // State Getters
    // ──────────────────────────────────────────────────────────────
    get isInitialized() {
        return this._initialized;
    }
    get isRunning() {
        return this._running;
    }
    /** Direct access to the MusicPlayer for play/pause/skip controls */
    get musicPlayer() {
        return this._musicPlayer;
    }
    /** Pause speech mid-utterance (used when the user starts a voice
     *  recording — prevents the bot's voice from contaminating the mic).
     *  Clears the pending advance timer so a fresh queue doesn't auto-start
     *  while the user is recording. */
    pauseSpeech() {
        if (this._advanceTimer !== null) {
            window.clearTimeout(this._advanceTimer);
            this._advanceTimer = null;
        }
        this._ttsPlayer.pause();
    }
    /** Live AudioContext + AnalyserNode for the TTS pipeline.
     *  Consumers (waveform UI, audio-health monitors) read from these to
     *  visualise the REAL audio signal — not a JS-timer pretending. */
    get audioContext() { return this._ttsPlayer.audioContext; }
    get ttsAnalyser() { return this._ttsPlayer.analyser; }
    get currentLocation() {
        return this._location.hasPosition ? this._location.snapshot() : null;
    }
    get currentTopic() {
        return this._topicCard.topic;
    }
    get currentMode() {
        return this._tracking.currentMode;
    }
    get currentTransport() {
        return this._transport;
    }
    get currentTrackId() {
        return this._specialTrack.trackId;
    }
    get sentCount() {
        return this._tracking.sentCount;
    }
    get remainingBudgetMin() {
        return this._budget.getRemainingMin();
    }
    /** Expose the underlying event manager for advanced / framework-adapter use. */
    get eventManager() {
        return this._bus;
    }
    /** Access to the VoiceRecorder for start/stop control from the UI. */
    get voiceRecorder() {
        return this._voice;
    }
    /**
     * Access to the Adventure manager — used by the React useAdventure hook.
     * Consumers not using React can also interact with it directly, though
     * the hook is the idiomatic interface.
     */
    get adventure() {
        return this._adventureManager;
    }
    /**
     * Access to the Story Points manager — used by the React useStoryPoints hook.
     */
    get storyPoints() {
        return this._storyPointsManager;
    }
    /**
     * Access to the Local State manager — used by Adventure/StoryPoints
     * internally, exposed for consumers who want to inspect or clear
     * persistent state programmatically (e.g. in a "clear all progress" button).
     */
    get localState() {
        return this._localState;
    }
    // ──────────────────────────────────────────────────────────────
    // Event Subscription
    // ──────────────────────────────────────────────────────────────
    /**
     * Subscribe to a curated public event. Returns an unsubscribe function.
     *
     * The internal event bus has many more events (from individual services),
     * but only these stable, curated events are part of the public API.
     */
    on(event, callback) {
        const internal = AudioGuide._mapEventNameToInternal(event);
        const cb = callback;
        this._bus.on(internal, cb);
        return () => this._bus.off(internal, cb);
    }
    static _mapEventNameToInternal(event) {
        switch (event) {
            case 'ready':
                return 'audio-guide:ready';
            case 'response':
                return 'iacp:response';
            case 'topic:changed':
                return 'topic:changed';
            case 'location:changed':
                return 'location:changed';
            case 'speech:start':
                return 'tts:start';
            case 'speech:end':
                return 'tts:end';
            case 'song:loaded':
                return 'music:started';
            case 'song:ended':
                return 'music:ended';
            case 'vibe:changed':
                return 'vibe:changed';
            case 'mode:changed':
                return 'audio-guide:mode-changed';
            case 'track:changed':
                return 'audio-guide:track-changed';
            case 'error':
                return 'audio-guide:error';
            default:
                return event;
        }
    }
    // ──────────────────────────────────────────────────────────────
    // Internal Orchestration
    // ──────────────────────────────────────────────────────────────
    _wireOrchestration() {
        const bus = this._bus;
        // Log only the FIRST GPS fix — subsequent ones are spammy.
        let firstFixLogged = false;
        bus.on('gps:update', (pos) => {
            // Manual mode: ignore internal GPS — user has chosen a position via long-press
            if (this._manualGpsMode)
                return;
            // D9.3: route through _setLocation, not direct _location.updatePosition,
            // so _acquireFullLocationContext gets triggered on the first fix.
            // The legacy direct path bypassed D9 entirely — first-fix would
            // emit gps:update, fire _sendAndListen via the "first-fix kick"
            // (Sabrina-fix), and the FIRST_UPDATE shipped with empty
            // street/city/region/places. D9's position-acquired event now
            // takes over the kick role (start() listens for it).
            this._setLocation(pos);
            if (!firstFixLogged) {
                firstFixLogged = true;
                const accStr = pos.accuracy !== null ? `±${Math.round(pos.accuracy)}m` : '?';
                const fixMsg = `GPS fix: ${pos.lat.toFixed(5)}, ${pos.lon.toFixed(5)} (${accStr})`;
                this._logger.info(fixMsg);
                this._remoteLog(fixMsg, 'gps');
            }
            // _setLocation above does updatePosition + mapRender +
            // _detectNearbyTrack + (D9-branching) geocode/osm prefetch.
            // Only heard prefetch is bus-handler-specific because it
            // depends on the heardCallback that consumers attach lazily.
            this._prefetchHeard(pos.lat, pos.lon);
        });
        bus.on('compass:update', (payload) => {
            this._location.updateCompass(payload.heading);
        });
        // External map focus request (e.g. warmup cache topic) — same path
        // the narrative focus uses for live Guide responses.
        bus.on('map:focus', (p) => {
            if (this._mapRenderer && p?.lat && p?.lon) {
                this._mapRenderer.focusOnLocation(p.lat, p.lon, p.label);
            }
        });
        bus.on('gps:error', (payload) => {
            this._logger.error(`GPS: ${payload.message}`);
            this._remoteLog(`GPS error: ${payload.message}`, 'gps', 'Error');
        });
        bus.on('iacp:response', (res) => this._handleIacpResponse(res));
        // Stash km_progress + phase from each live-send roundtrip so we
        // can pair the next IACP response with the km it was anchored to
        // (km-at-delivery for the delivered-log).
        bus.on('iacp:send-ok', (payload) => {
            if (payload.kmProgress !== undefined)
                this._lastTrackKm = payload.kmProgress ?? null;
            if (payload.phase !== undefined)
                this._lastPhase = payload.phase ?? null;
        });
        bus.on('music:ended', () => {
            this._remoteLog('Music: song ended (queue advances automatically)', 'music');
        });
        bus.on('music:started', (payload) => {
            this._remoteLog(`Music: playing "${payload.title}" (${payload.videoId})`, 'music');
        });
        bus.on('music:ended', () => {
            this._remoteLog('Music: song ended', 'music');
        });
        bus.on('music:load-failed', (payload) => {
            this._remoteLog(`Music: load failed "${payload.query}" — ${payload.error || '?'}`, 'music', 'Warning');
        });
        bus.on('tts:start', () => {
            this._remoteLog('TTS: playback started', 'tts');
        });
        bus.on('tts:end', () => {
            this._remoteLog('TTS: playback ended', 'tts');
            // Emit package:end with the title of the package that JUST finished,
            // BEFORE advancing the queue, so main.ts audio archival can attach
            // the MP3 to the right interaction id.
            if (this._activePackage) {
                const endedTitle = this._activePackage.response.topic?.title || '';
                bus.emit('package:end', { title: endedTitle, label: this._activePackage.label });
            }
            // Content Package Queue advancement rules (matches user-requested UX):
            //   - After 'main': ALWAYS play 'followup' if available — guaranteed
            //     2-story chain even if a fresher GPS response is already waiting.
            //   - After 'followup': pending response wins over 'filler' — fresh
            //     location content beats the 3rd-best-effort filler.
            //   - After 'filler': only pending response can play (nothing else left).
            //   - When switching to pending response: drain leftover additionals of
            //     the previous response, then move pending's additionals in.
            const endedKind = this._activePackage?.kind ?? 'main';
            let next = null;
            this._remoteLog(`Queue state at tts:end: ended=${endedKind} addQ=${this._additionalQueue.length} pending=${this._pendingPackage ? 'yes' : 'no'} pendingAdd=${this._pendingAdditionals.length}`, 'queue');
            // Step 1 — after main, try followup first. followupBeatsPending=true (default)
            // → followup always wins (classic 2-story chain). =false (deep_dive) → a fresh
            // POI (pending) beats the deeper layer, so deeper layers play ONLY when nothing
            // new is available (lingering) — exactly Alex' "solange kein anderer Beitrag da".
            if (endedKind === 'main' &&
                this._additionalQueue.length > 0 &&
                (this._playerBehavior.followupBeatsPending || !this._pendingPackage)) {
                next = this._additionalQueue.shift();
            }
            // Step 2 — try pending (fresh response). After followup or filler.
            if (!next && this._pendingPackage) {
                next = this._pendingPackage;
                this._pendingPackage = null;
                // Previous response's leftover additionals are abandoned —
                // user is moving on, those are stale.
                this._additionalQueue = this._pendingAdditionals;
                this._pendingAdditionals = [];
            }
            // Step 3 — no pending, no followup-after-main → try filler (only if
            // we weren't already on filler, to prevent re-running).
            if (!next && endedKind !== 'filler' && this._additionalQueue.length > 0) {
                next = this._additionalQueue.shift();
            }
            if (next) {
                this._activePackage = next;
                this._applyPackageVisuals(next);
                this._remoteLog(`Package advanced [${next.kind || 'main'}]: "${next.response.topic?.title || next.label}"`, 'queue');
                this._refreshUpcomingMarkers();
                // 4s breather between back-to-back TTS. The followup decision was
                // committed above (next=followup, _pendingPackage stays untouched),
                // so even if a new IACP response arrives during the pause, the
                // followup still wins — the new response simply replaces
                // _pendingPackage and plays AFTER the followup ends.
                if (next.ttsBuffer) {
                    if (this._advanceTimer !== null)
                        window.clearTimeout(this._advanceTimer);
                    const pkgToSpeak = next;
                    this._advanceTimer = window.setTimeout(() => {
                        this._advanceTimer = null;
                        // Guard: guide may have been stopped/paused during the pause
                        if (!this._running || this._activePackage !== pkgToSpeak)
                            return;
                        this._ttsPlayer.speakBuffer(pkgToSpeak.ttsBuffer, pkgToSpeak.label);
                    }, this._playerBehavior.layerPauseMs);
                }
            }
            else {
                this._activePackage = null;
                if (this._mapRenderer)
                    this._mapRenderer.renderUpcomingTopics([]);
                // Surprise mode: queue ran dry → after a short breather, teleport
                // to the next random world location. Skips when paused/stopped.
                if (this._surpriseMode && this._running) {
                    window.setTimeout(() => {
                        if (this._surpriseMode && this._running && !this._activePackage) {
                            void this._pickSurpriseLocation();
                        }
                    }, AudioGuide.QUEUE_PAUSE_MS);
                }
            }
        });
    }
    /** Handle a song recommendation from Guide — play immediately or queue it. */
    _handleSongRecommendation(query) {
        if (!this._musicPlayer.isPlaying && !this._songQueue.hasCurrent) {
            void this._musicPlayer.loadAndPlay(query, undefined, undefined, undefined, 'ai');
            return;
        }
        this._songQueue.enqueue({ query, source: 'ai' });
        this._logger.info(`♪ Queued: ${query} (${this._songQueue.queueLength} pending)`);
    }
    _throttledReverseGeocode(lat, lon) {
        const now = Date.now();
        if (now - this._lastGeocodeTs < AudioGuide.GEOCODE_THROTTLE_MS)
            return;
        this._lastGeocodeTs = now;
        void this._geocoder.lookup(lat, lon).then((addr) => {
            if (addr) {
                this._location.updateAddress(addr.street, addr.city, addr.suburb, addr.region, addr.country, addr.countryCode ?? null);
            }
        });
    }
    /**
     * Prefetch OSM context in the background on every GPS update.
     * Calls the artrack API `/osm/nearby/compact` endpoint which has
     * server-side caching (~110m grid, 1h TTL, Overpass mirror failover).
     *
     * The result is stored in _osmPrefetch and read synchronously
     * by _sendAndListen — zero added latency to the IACP send.
     */
    _prefetchOsmContext(lat, lon) {
        if (!this._osmEnabled || this._osmPrefetching)
            return;
        const radius = this.osmRadius;
        if (radius > 2000)
            return;
        // Check if we've moved enough to warrant a new fetch (~110m grid, matching server cache)
        if (this._osmPrefetch) {
            const sameCell = Math.abs(lat - this._osmPrefetch.lat) < 0.001
                && Math.abs(lon - this._osmPrefetch.lon) < 0.001
                && radius === this._osmPrefetch.radius;
            if (sameCell)
                return;
        }
        this._osmPrefetching = true;
        const apiBase = this._config.osmApiBase || 'https://api-artrack.arkturian.com';
        const url = `${apiBase}/places/nearby/compact?lat=${lat.toFixed(5)}&lng=${lon.toFixed(5)}&radius_m=${radius}`;
        void fetch(url, { headers: { 'X-API-KEY': 'Inetpass1' } })
            .then((r) => {
            if (!r.ok)
                throw new Error(`HTTP ${r.status}`);
            return r.json();
        })
            .then((data) => {
            this._osmPrefetching = false;
            if (!this._running)
                return;
            if (data.text && data.count > 0) {
                this._osmPrefetch = {
                    tag: ` [PLACES:${radius}m,${data.count}] ${data.text}`,
                    lat, lon, radius, at: Date.now(),
                };
            }
            else {
                this._osmPrefetch = {
                    tag: ` [RADIUS:${radius}m]`,
                    lat, lon, radius, at: Date.now(),
                };
            }
        })
            .catch(() => {
            this._osmPrefetching = false;
        });
    }
    _prefetchHeard(lat, lon) {
        if (!this._heardCallback) {
            if (!this._heardCallbackLogged) {
                this._remoteLog('HEARD: no callback set (user not logged in?)', 'heard', 'Warning');
                this._heardCallbackLogged = true;
            }
            return;
        }
        // Check if cached result is still valid (~500m grid, 5min)
        if (this._heardCache) {
            const sameCell = Math.abs(lat - this._heardCache.lat) < 0.005
                && Math.abs(lon - this._heardCache.lon) < 0.005;
            if (sameCell && Date.now() - this._heardCache.at < 300000)
                return;
        }
        void this._heardCallback(lat, lon).then(heard => {
            if (!this._running)
                return;
            this._heardCache = { tag: heard, lat, lon, at: Date.now() };
            if (heard) {
                this._remoteLog(`HEARD prefetch: "${heard.slice(0, 100)}"`, 'heard');
            }
        }).catch((err) => {
            this._remoteLog(`HEARD prefetch failed: ${err}`, 'heard', 'Error');
        });
    }
    /**
     * Auto-detect nearby artrack tracks via snap-to-path.
     * If user enters a track's proximity, auto-activate it.
     * If user leaves, auto-deactivate back to generic mode.
     */
    async _detectNearbyTrack(lat, lon) {
        if (!this._trackDetectCallback || this._trackDetectPending)
            return;
        // Skip if user manually set a track (don't override manual choice)
        if (this._specialTrack.isActive && this._autoTrackId === null)
            return;
        this._trackDetectPending = true;
        try {
            const trackId = await this._trackDetectCallback(lat, lon);
            if (!this._running)
                return;
            if (trackId && this._autoTrackId !== trackId) {
                // Entered a track's proximity — auto-activate
                this._autoTrackId = trackId;
                this.setTrack(trackId);
                this._remoteLog(`Auto-track: entered track ${trackId}`, 'track');
                this._bus.emit('track:auto-detected', { trackId });
            }
            else if (!trackId && this._autoTrackId !== null) {
                // Left all tracks — auto-deactivate
                this._remoteLog(`Auto-track: left track ${this._autoTrackId}`, 'track');
                this._autoTrackId = null;
                this.setTrack(null);
                this._bus.emit('track:auto-detected', { trackId: null });
            }
        }
        catch {
            // swallow — caller doesn't care about errors, just timing
        }
        finally {
            this._trackDetectPending = false;
        }
    }
    /**
     * Translate user-facing fields of an IACP response in place using
     * the registered translator callback. Batches all strings into
     * one round-trip for efficiency.
     *
     * Translated fields:
     *   res.content
     *   res.vibe (string or {text, intensity})
     *   res.topic.subtitle  (title stays — Eigennamen)
     *   res.additional_topics[].content
     *   res.additional_topics[].vibe
     *   res.additional_topics[].topic.subtitle
     *
     * If the translator returns fewer items than expected, the
     * remaining fields are left untouched (silent partial failure
     * beats noisy hard failure for live audio).
     */
    async _translateResponseInPlace(res, targetLang) {
        const cb = this._translatorCallback;
        if (!cb)
            return;
        const slots = [];
        const inputs = [];
        const queue = (text, apply) => {
            const s = (text || '').trim();
            if (!s)
                return;
            const idx = inputs.length;
            inputs.push(s);
            slots.push(() => {
                const out = translated[idx];
                if (out)
                    apply(out);
            });
        };
        let translated = [];
        // Main response
        queue(res.content, (t) => { res.content = t; });
        if (typeof res.vibe === 'string') {
            queue(res.vibe, (t) => { res.vibe = t; });
        }
        else if (res.vibe && typeof res.vibe === 'object') {
            queue(res.vibe.text, (t) => {
                if (typeof res.vibe === 'object' && res.vibe)
                    res.vibe.text = t;
            });
        }
        if (res.topic?.subtitle) {
            queue(res.topic.subtitle, (t) => {
                if (res.topic)
                    res.topic.subtitle = t;
            });
        }
        // additional_topics
        if (Array.isArray(res.additional_topics)) {
            for (const at of res.additional_topics) {
                queue(at.content, (t) => { at.content = t; });
                if (typeof at.vibe === 'string') {
                    queue(at.vibe, (t) => { at.vibe = t; });
                }
                else if (at.vibe && typeof at.vibe === 'object') {
                    queue(at.vibe.text, (t) => {
                        if (typeof at.vibe === 'object' && at.vibe)
                            at.vibe.text = t;
                    });
                }
                if (at.topic?.subtitle) {
                    queue(at.topic.subtitle, (t) => {
                        if (at.topic)
                            at.topic.subtitle = t;
                    });
                }
            }
        }
        if (inputs.length === 0)
            return;
        translated = await cb(inputs, targetLang);
        for (const slot of slots)
            slot();
        this._remoteLog(`Translated ${inputs.length} fields → ${targetLang}`, 'translator');
    }
    async _handleIacpResponse(res) {
        if (!this._running) {
            this._logger.warn('Discarding IACP response — guide is stopped');
            return;
        }
        // Infra-Fehlertexte sind KEINE Narration. Prod-Vorfall 2026-07-03: ein
        // ausgeloggter Pool-Agent lieferte „Please run /login · API Error: 401
        // Invalid authentication credentials" als Response — stand auf der Card
        // und wurde dem User per TTS VORGELESEN. Solche Strings werden laut
        // geloggt + als Fehler gemeldet, aber niemals gesprochen/angezeigt.
        // (Die saubere Stelle ist server-seitig im queue_worker — das hier ist
        // Defense-in-Depth am letzten Glied vor dem Ohr des Users.)
        const infraError = /please run \/login|api error:?\s*\d{3}|invalid authentication credentials|invalid api key|credit balance is too low/i;
        if (res.content && infraError.test(res.content)) {
            this._logger.error(`Discarding infra-error response (not narration): ${res.content.slice(0, 160)}`);
            this._remoteLog(`Infra-Fehler als Response verworfen (Agent-Session defekt?): ${res.content.slice(0, 160)}`, 'iacp', 'Error');
            this._bus.emit('audio-guide:error', new Error(`agent session unhealthy: ${res.content.slice(0, 120)}`));
            return;
        }
        // Stamp the delivered-log so the next outgoing message tells the
        // bot what's already been said + at what km. Only stamp once per
        // response and only when topic has a usable title; abstract topics
        // (no title) don't merit a memory entry. Cap to MAX_DELIVERED_ENTRIES.
        const tTitle = res.topic?.title?.trim();
        if (tTitle) {
            this._deliveredLog.push({
                title: tTitle,
                kid: typeof res.topic?.knowledge_id === 'number' ? res.topic.knowledge_id : null,
                km: this._lastTrackKm,
            });
            if (this._deliveredLog.length > AudioGuide.MAX_DELIVERED_ENTRIES) {
                this._deliveredLog.splice(0, this._deliveredLog.length - AudioGuide.MAX_DELIVERED_ENTRIES);
            }
        }
        // Inject the user's uploaded photo as the topic-card image when this
        // response is the answer to a photo-question. Skip topic-image
        // enrichment for this package — the user's own picture wins.
        if (this._pendingUserPhotoUrl) {
            if (!res.topic)
                res.topic = { title: '', subtitle: '' };
            res.topic.image = this._pendingUserPhotoUrl;
            // Mark on the response so _applyPackageVisuals knows to skip the
            // Wikipedia/Commons/Places lookup chain that would replace the image.
            res.__skipImageEnrichment = true;
            this._remoteLog(`📷 Using user photo as topic image: ${this._pendingUserPhotoUrl}`, 'photo');
            this._pendingUserPhotoUrl = null;
        }
        // ── Translate response if a translator is wired + user is on non-DE ──
        // Bot generates DE master; consumer translates per UI language so
        // both the topic-card text and the TTS-speak text come out in the
        // user's chosen tongue. topic.title is NEVER translated — Eigennamen
        // (Lendkanal, Minimundus, ...) should remain literal.
        if (this._translatorCallback && this._language && this._language !== 'de') {
            try {
                await this._translateResponseInPlace(res, this._language);
            }
            catch (err) {
                // Translator down → fall back to DE master so the user at least
                // gets content, not silence. Log loudly so the issue surfaces.
                this._logger.warn(`Translator failed for lang=${this._language}, serving DE master: ${err}`);
                this._remoteLog(`Translator failed (lang=${this._language}) — serving DE`, 'translator', 'Warning');
            }
        }
        // Log response summary
        const parts = [];
        if (res.content !== undefined) {
            const preview = res.content.trim().slice(0, 60).replace(/\s+/g, ' ');
            parts.push(`content="${preview}${res.content.length > 60 ? '…' : ''}"`);
        }
        if (res.topic)
            parts.push(`topic="${res.topic.title || '?'}"`);
        if (res.destination)
            parts.push(`dest="${res.destination.label || '?'}"`);
        if (res.pois && res.pois.length > 0)
            parts.push(`pois=${res.pois.length}`);
        if (res.song)
            parts.push(`song="${res.song}"`);
        if (res.vibe) {
            const vibeStr = typeof res.vibe === 'string' ? res.vibe : res.vibe.text || '?';
            parts.push(`vibe="${vibeStr}"`);
        }
        if (res.storyPoints && res.storyPoints.length > 0) {
            parts.push(`storyPoints=${res.storyPoints.length}`);
        }
        if (Array.isArray(res.additional_topics)) {
            parts.push(`additional=${res.additional_topics.length}`);
        }
        const responseSummary = parts.length > 0 ? parts.join(' | ') : '(empty)';
        this._logger.info(`← IACP response: ${responseSummary}`);
        this._remoteLog(`← Response: ${responseSummary}`, 'iacp');
        // ── Build Content Package ──
        const textToSpeak = res.content || '';
        const label = textToSpeak.length > 50 ? textToSpeak.slice(0, 50) + '…' : textToSpeak || '(no content)';
        const pkg = { response: res, ttsBuffer: null, label, kind: 'main' };
        // Independent fields: songs + storyPoints don't need visual sync
        if (res.song) {
            this._remoteLog(`Guide empfiehlt Song: "${res.song}"`, 'youtube');
            this._handleSongRecommendation(res.song);
            this._lastSongAt = Date.now();
        }
        if (res.storyPoints && res.storyPoints.length > 0) {
            this._storyPointsManager.loadStoryPoints(res.storyPoints);
        }
        // ── Fetch TTS audio (async) ──
        if (textToSpeak.trim()) {
            // Abort previous in-flight fetch if a new response arrives
            if (this._ttsFetchAbort)
                this._ttsFetchAbort.abort();
            this._ttsFetchAbort = new AbortController();
            try {
                this._remoteLog(`TTS fetching: "${textToSpeak.slice(0, 80)}…"`, 'tts');
                pkg.ttsBuffer = await this._ttsPlayer.fetchAudio(textToSpeak, this._ttsFetchAbort.signal);
                this._ttsFetchAbort = null;
            }
            catch (err) {
                this._ttsFetchAbort = null;
                if (err.name === 'AbortError') {
                    this._remoteLog('TTS fetch aborted (newer response)', 'tts', 'Warning');
                    return; // Superseded by newer response — discard this package
                }
                this._remoteLog(`TTS fetch failed: ${err.message}`, 'tts', 'Error');
                // No audio — still apply visuals immediately (graceful degradation)
                this._applyPackageVisuals(pkg);
                return;
            }
        }
        else {
            this._logger.warn('No content field in response — skipping TTS');
            this._remoteLog('No content in response — TTS skipped', 'iacp', 'Warning');
            // No TTS — apply visuals immediately
            this._applyPackageVisuals(pkg);
            return;
        }
        // ── Queue the main package + route additional_topics correctly ──
        // Additionals MUST attach to their own response (not the active one),
        // otherwise B's followup gets queued behind A's filler and the priority
        // logic in tts:end breaks.
        const additionals = Array.isArray(res.additional_topics) ? res.additional_topics : [];
        if (!this._audioQueue.isPlaying && !this._activePackage) {
            // Nothing playing — activate immediately
            this._activePackage = pkg;
            this._applyPackageVisuals(pkg);
            this._ttsPlayer.speakBuffer(pkg.ttsBuffer, pkg.label);
            this._remoteLog(`Package active: "${res.topic?.title || label}"`, 'queue');
            // additionals belong to the ACTIVE response → push into active queue
            if (additionals.length > 0) {
                this._refreshUpcomingMarkers(additionals);
                void this._enqueueAdditionalTopics(additionals, 'active');
            }
            else if (this._mapRenderer) {
                this._mapRenderer.renderUpcomingTopics([]);
            }
        }
        else {
            // Something is playing — queue as pending (drop old pending + its additionals)
            if (this._pendingPackage) {
                this._remoteLog(`Package dropped: "${this._pendingPackage.label}"`, 'queue', 'Warning');
            }
            this._pendingPackage = pkg;
            this._pendingAdditionals = []; // reset pending-additionals for the new pending
            this._remoteLog(`Package pending: "${res.topic?.title || label}"`, 'queue');
            // additionals belong to the PENDING response → fetch into pending queue
            // so they're ready by the time pending activates
            if (additionals.length > 0) {
                void this._enqueueAdditionalTopics(additionals, 'pending');
            }
        }
    }
    /** Render the topics that are still waiting in the additional queue. */
    _refreshUpcomingMarkers(fromResponse) {
        if (!this._mapRenderer)
            return;
        // Prefer current queue state; otherwise initialise from the IACP response
        // (used right when the response arrives, before TTS fetches finish).
        const source = fromResponse
            ? fromResponse.map(at => ({
                lat: at.topic?.lat,
                lon: at.topic?.lon,
                title: at.topic?.title || '',
            }))
            : this._additionalQueue.map(pkg => ({
                lat: pkg.response.topic?.lat,
                lon: pkg.response.topic?.lon,
                title: pkg.response.topic?.title || '',
            }));
        const upcoming = source.filter((t) => typeof t.lat === 'number' && typeof t.lon === 'number');
        this._mapRenderer.renderUpcomingTopics(upcoming);
    }
    /** Fetch TTS for additional_topics in parallel and queue them.
     *  `target='active'` pushes to _additionalQueue (current response's followup/filler).
     *  `target='pending'` pushes to _pendingAdditionals (waits for pending main to activate). */
    async _enqueueAdditionalTopics(additional, target = 'active') {
        const startGen = this._stopGeneration;
        for (let i = 0; i < additional.length; i++) {
            const at = additional[i];
            if (!at?.content?.trim())
                continue;
            // Build a synthetic IacpResponse so the visuals pipeline works
            const synthRes = {
                id: 0,
                content: at.content,
                vibe: at.vibe,
                topic: at.topic,
            };
            const label = at.content.length > 50 ? at.content.slice(0, 50) + '…' : at.content;
            // First additional is the guaranteed 'followup', second is the optional 'filler'.
            const kind = i === 0 ? 'followup' : 'filler';
            const subPkg = {
                response: synthRes,
                ttsBuffer: null,
                label,
                kind,
            };
            try {
                // Use a fresh AbortController so the main TTS abort doesn't kill these
                const ctrl = new AbortController();
                subPkg.ttsBuffer = await this._ttsPlayer.fetchAudio(at.content, ctrl.signal);
            }
            catch (err) {
                this._remoteLog(`Additional TTS fetch failed (${kind}): ${err.message}`, 'tts', 'Warning');
                continue;
            }
            // If guide was stopped/restarted while we were fetching, abandon
            if (startGen !== this._stopGeneration || !this._running)
                return;
            if (target === 'pending') {
                this._pendingAdditionals.push(subPkg);
            }
            else {
                this._additionalQueue.push(subPkg);
            }
            this._remoteLog(`Additional queued [${kind}, ${target}]: "${at.topic?.title || label}"`, 'queue');
        }
    }
    /**
     * Apply the visual side of a content package — topic card, map, POIs, focus.
     * Called when a package starts playing (not when the IACP response arrives).
     */
    _applyPackageVisuals(pkg) {
        const res = pkg.response;
        // Emit the package activation BEFORE other visuals so consumers
        // (responseText, debug HUDs, etc.) can swap their text in sync with
        // the audio that's about to play. Without this, the bottom-panel
        // text stays on Topic #1 while TTS for Topic #2 plays.
        this._bus.emit('package:start', {
            content: res.content || '',
            title: res.topic?.title || '',
            kind: pkg.kind || 'main',
        });
        if (res.destination && this._mapRenderer) {
            this._mapRenderer.setDestination(res.destination.lat, res.destination.lon, res.destination.label);
        }
        if (res.pois && res.pois.length > 0 && this._mapRenderer) {
            this._mapRenderer.renderPOIs(res.pois);
        }
        if (res.topic) {
            // Sanitize Wikipedia URL
            if (res.topic.wikipedia) {
                const url = res.topic.wikipedia;
                const protocolEnd = url.indexOf('://');
                if (protocolEnd > 0) {
                    res.topic.wikipedia = url.slice(0, protocolEnd + 3) + url.slice(protocolEnd + 3).replace(/ /g, '');
                }
            }
            const skipImageEnrich = res.__skipImageEnrichment === true;
            if (!skipImageEnrich) {
                if (res.topic.image && !WikipediaEnricher.isPhotoUrl(res.topic.image)) {
                    this._remoteLog(`Topic image filtered (bot-provided): ${res.topic.image}`, 'wiki', 'Warning');
                    res.topic.image = undefined;
                }
                // Don't show the same image twice in one session
                if (res.topic.image && this._shownImages.has(res.topic.image)) {
                    this._remoteLog(`Image already shown this session, skipping: ${res.topic.image}`, 'wiki', 'Warning');
                    res.topic.image = undefined;
                }
                if (res.topic.image)
                    this._shownImages.add(res.topic.image);
                // Always enrich: even if bot provided image+description+wikipedia,
                // we need to validate the wikipedia URL exists (bots hallucinate URLs)
                // and check for a better cached image from history.
                const needsEnrich = true;
                this._topicCard.setTopic(res.topic, needsEnrich);
                this._remoteLog(`Topic: "${res.topic.title}" wiki=${res.topic.wikipedia || 'none'} img=${res.topic.image || 'none'}`, 'topic');
                void this._enrichTopicFromWikipedia(res.topic);
            }
            else {
                // Photo-question response — show the user's photo as-is, hero size,
                // no Wikipedia/Commons/Places lookups that could replace it.
                this._topicCard.setTopic(res.topic, false);
                this._remoteLog(`Topic: "${res.topic.title}" img=user-photo (enrichment skipped)`, 'topic');
            }
        }
        if (this._mapRenderer) {
            const focus = this._pickNarrativeFocus(res);
            if (focus) {
                this._mapRenderer.focusOnLocation(focus.lat, focus.lon, focus.label);
                // First correction: Nominatim forward-geocode of the topic name (fast,
                // but fuzzy — the name may be the bot's guess, possibly wrong-language).
                // Tight 2km radius: a lake/town centroid must NOT be accepted as „the
                // fortress". The stronger correction is the verified Wikipedia article
                // coordinate in _enrichTopicFromWikipedia, which runs next and overrides
                // this with the real entity's position (same one shown as the photo).
                if (focus.label) {
                    const snap = this._location.snapshot();
                    const epoch = this._topicCard.epoch;
                    void this._geocoder.geocode(focus.label, snap.lat ?? undefined, snap.lon ?? undefined).then(geo => {
                        if (!geo || !this._mapRenderer || epoch !== this._topicCard.epoch)
                            return;
                        const dist = AudioGuide._haversineMeters(snap.lat ?? 0, snap.lon ?? 0, geo.lat, geo.lon);
                        if (dist < 2000) {
                            this._mapRenderer.focusOnLocation(geo.lat, geo.lon, focus.label);
                            this._remoteLog(`Geocode: "${focus.label}" corrected ${Math.round(dist)}m from bot guess`, 'geocode');
                        }
                    });
                }
            }
        }
    }
    _pickNarrativeFocus(res) {
        const candidates = [];
        if (res.topic && typeof res.topic.lat === 'number' && typeof res.topic.lon === 'number') {
            candidates.push({ lat: res.topic.lat, lon: res.topic.lon, label: res.topic.title });
        }
        if (res.destination) {
            candidates.push({
                lat: res.destination.lat,
                lon: res.destination.lon,
                label: res.destination.label,
            });
        }
        if (res.pois && res.pois.length > 0) {
            const first = res.pois[0];
            if (typeof first.lat === 'number' && typeof first.lon === 'number') {
                candidates.push({ lat: first.lat, lon: first.lon, label: first.title });
            }
        }
        const userSnap = this._location.snapshot();
        for (const cand of candidates) {
            if (userSnap.lat !== null && userSnap.lon !== null) {
                const distKm = AudioGuide._haversineMeters(userSnap.lat, userSnap.lon, cand.lat, cand.lon) / 1000;
                if (distKm > AudioGuide.MAX_FOCUS_DISTANCE_KM)
                    continue;
            }
            return cand;
        }
        return null;
    }
    async _sendAndListen(isDriftRequest) {
        // Mode ② Statisch/ArTrack: the deterministic narration_point corpus is
        // the only source — never talk to the realtime agent.
        if (this._engineMode !== 'normal') {
            // Realtime mode (Content #1196): the GPS trigger drives the realtime
            // session instead of the pool agent — SAME cadence, low-latency engine.
            // The normal pool-agent+TTS path is paused; persist/corpus/UI stay shared.
            if (this._engineMode === 'realtime' && this._realtimeSession?.running) {
                this.narrateRealtimeLocation();
            }
            this._bus.emit('audio-guide:send-skipped', {
                reason: this._engineMode === 'realtime' ? 'realtime-mode' : 'corpus-static-mode',
            });
            return;
        }
        if (!this._location.hasPosition) {
            this._logger.warn('_sendAndListen skipped — no GPS position yet (gpsSource=' + this._config.gpsSource + ')');
            this._bus.emit('audio-guide:send-skipped', { reason: 'no-position' });
            return;
        }
        // FIRST_UPDATE enrichment is now done up-front in
        // _acquireFullLocationContext() (D9 Bootstrap-Acquire). By the
        // time we get here, snap.city/region + _osmPrefetch are already
        // populated (or have hit their 3s timeouts). No race-fix needed.
        const snap = this._location.snapshot();
        const lat = snap.lat;
        const lon = snap.lon;
        const heading = snap.effectiveHeading;
        const dir = heading !== null ? AudioGuide._bearingName(heading) : '?';
        const kmh = snap.speed !== null ? Math.round(snap.speed * 3.6) : '?';
        const ts = new Date().toLocaleTimeString('de-DE', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        const street = snap.street || '-';
        const ctxTag = this._drift.computeContextTag(lat, lon);
        const driftFlag = isDriftRequest ? ' [DRIFT REQUEST]' : '';
        const trackTag = this._specialTrack.isActive
            ? ` [TRACK:${this._specialTrack.trackId}]`
            : '';
        // Persona tag: only emit when != 'default' so old bots that
        // don't know the tag still get the (matching) default behaviour.
        const personaTag = this._narrator !== 'default'
            ? ` [PERSONA:${this._narrator}]`
            : '';
        // Interests tag: ONLY in Generic mode (no SpecialTrack). When a
        // track is active, the operator-curated dimensions from artrack
        // metadata outrank user curiosity — we don't even ship the tag.
        const interestSlugs = this._interestsState.current;
        const interestsTag = !this._specialTrack.isActive && interestSlugs.length
            ? ` [INTERESTS:${interestSlugs.join(',')}]`
            : '';
        this._remoteLog(`[SEND] trackActive=${this._specialTrack.isActive} trackId=${this._specialTrack.trackId} ` +
            `narrator=${this._narrator} interests=[${interestSlugs.join(',')}] ` +
            `tag="${trackTag.trim()}${personaTag}${interestsTag}"`, 'track');
        const songReady = this._lastSongAt === null || Date.now() - this._lastSongAt > AudioGuide.SONG_RHYTHM_MS;
        const songTag = songReady ? ' [SONG_READY]' : '';
        const accTag = snap.accuracy !== null ? ` [ACC:${Math.round(snap.accuracy)}m]` : '';
        const headingReliable = snap.compassHeading !== null || (snap.speed ?? 0) * 3.6 > 5;
        const headingTag = headingReliable ? ' [HEADING:RELIABLE]' : '';
        let nearTag = '';
        let osmTag = '';
        const speedKmh = snap.speed !== null ? snap.speed * 3.6 : 0;
        if (this._osmEnabled) {
            const radius = this.osmRadius;
            if (radius <= 2000 && this._osmPrefetch) {
                // Use pre-fetched OSM context (populated async on GPS updates — zero latency)
                osmTag = this._osmPrefetch.tag;
            }
            else if (radius > 2000) {
                // Large radius — inspiration mode, just tell the AI the range
                const km = Math.round(radius / 1000);
                osmTag = ` [RADIUS:${km}km]`;
            }
            else {
                // No prefetch available yet — send radius hint only
                osmTag = ` [RADIUS:${radius}m]`;
            }
        }
        else if (speedKmh < 20) {
            // Legacy single-nearest mode (default when OSM context is off)
            const near = await this._buildingResolver.findNearest(lat, lon);
            if (near) {
                nearTag = ` [NEAR:${near.name} (${near.kind}, ${near.distanceM}m)]`;
            }
        }
        // XML message builder — matches the schema documented in
        // _templates/guide.md (Content-API post #826). Replaces the
        // legacy "Transport: walking | Theme: ..." tag-string format
        // with structured XML so the bot consumes both Realtime and
        // Simulator inputs in the same shape.
        //
        // Sticky-Discipline: <session> with persona / interests /
        // theme / age / lang / intent / artrack is only emitted on
        // FIRST_UPDATE or when any sticky-field changed since the
        // last send. _sessionSnapshot tracks the last-sent values.
        const xmlEscape = (s) => s.replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
        // Decide whether this update is a FIRST_UPDATE based on whether
        // we've ever sent a session-snapshot before.
        const isFirstUpdate = this._sessionSnapshot === null;
        // ctxValue is the bare context tag without brackets. The legacy
        // ctxTag includes brackets like " [STATIONARY:5min]" — strip them.
        const ctxBareMatch = (ctxTag || ' [CRUISING]').match(/\[([^\]]+)\]/);
        const ctxValue = isFirstUpdate
            ? 'FIRST_UPDATE'
            : (ctxBareMatch ? ctxBareMatch[1] : 'CRUISING');
        // Build the current session-snapshot for comparison.
        const currentSnapshot = {
            trackId: this._specialTrack.isActive ? (this._specialTrack.trackId ?? null) : null,
            persona: this._narrator,
            clientId: this._clientId,
            // Interests are emitted ALSO on active tracks (changed 2026-06-12,
            // Alex): the user picked them in the UI — silently dropping them
            // made the bot blind to user curiosity ("focus war nur geschichte
            // obwohl es mehrere auswahlen gab"). The bot weighs them against
            // the track's operator dimensions; guide.md allows 0..n <focus>.
            interests: interestSlugs.join(','),
            // theme = settings-picker mode (culture/food/geology/...). Suppressed
            // when the user explicitly picked a multi-chip interest set OR the
            // round-robin path is active (userIntents) — otherwise <focus>culture</focus>
            // gets dragged in from the default tracking mode on top of the picked
            // interests (double-counted theme the bot can't reconcile).
            theme: (this._userIntents.length > 0 || interestSlugs.length > 0)
                ? '' : this._tracking.currentMode,
            age: this._userAge,
            lang: this._language,
            intent: this._currentIntentTag().replace(/^\s*\[INTENT:|\]$/g, '').trim(),
        };
        // Detect change for sticky-suppression. On first send we always
        // emit the session block; on subsequent sends only when a field
        // differs from the last snapshot.
        const sessionChanged = isFirstUpdate || (this._sessionSnapshot !== null && (this._sessionSnapshot.trackId !== currentSnapshot.trackId ||
            this._sessionSnapshot.persona !== currentSnapshot.persona ||
            this._sessionSnapshot.clientId !== currentSnapshot.clientId ||
            this._sessionSnapshot.interests !== currentSnapshot.interests ||
            this._sessionSnapshot.theme !== currentSnapshot.theme ||
            this._sessionSnapshot.age !== currentSnapshot.age ||
            this._sessionSnapshot.lang !== currentSnapshot.lang ||
            this._sessionSnapshot.intent !== currentSnapshot.intent));
        // Build the XML message. client_id is emitted as a top-level
        // attribute on every <gps_update> — bypasses the sticky-session
        // discipline so the bot can disambiguate concurrent client
        // conversations on every single tick, not only at FIRST_UPDATE.
        // The same id is also redundantly in <session><client_id> when
        // that block is emitted, but the attribute is the authoritative,
        // always-present source.
        const parts = [
            `<gps_update timestamp="${xmlEscape(ts)}" client_id="${xmlEscape(this._clientId)}">`,
        ];
        parts.push(`  <pos lat="${lat.toFixed(5)}" lon="${lon.toFixed(5)}"/>`);
        parts.push(`  <context>${xmlEscape(ctxValue)}</context>`);
        parts.push(`  <transport>${xmlEscape(this._transport)}</transport>`);
        // Single <location> tag — one human-readable description of where
        // the user IS, not a list of options. Replaces the legacy
        // <street>/<suburb>/<city>/<region> tags + <places> list that
        // forced the bot to choose between 20 nearby businesses.
        // Real-device 2026-06-08 (Heimstättenstraße): bot fell into
        // "St. Martin" because that was the nearest <place neighborhood>,
        // even though Nominatim knew suburb="Waidmannsdorf". And 16 of 20
        // <places> entries were business noise (Frisierstube, Marketing
        // GmbH, etc.) that the bot ignored. Phase 1 fix: emit ONE clear
        // location string. Phase 2 will replace <places> entirely with a
        // map image.
        const locationDesc = this._buildLocationDescription(snap, osmTag);
        // Delta: emit <location> only when text actually changed since last
        // tick. Same suburb/street description doesn't need to re-arrive at
        // the bot every 60s — it's already in the conversation context.
        // First send (snapshot null) or text-change → emit.
        if (locationDesc && locationDesc !== this._locationSnapshot) {
            parts.push(`  <location>${xmlEscape(locationDesc)}</location>`);
        }
        // Direction + speed only when we have real values. Drop the "?"
        // placeholder string from the legacy format.
        if (heading !== null) {
            const deg = Math.round(heading);
            parts.push(`  <dir deg="${deg}">${xmlEscape(dir)}</dir>`);
        }
        if (snap.speed !== null && typeof kmh === 'number') {
            parts.push(`  <speed_kmh>${kmh}</speed_kmh>`);
        }
        // Accuracy staffel: skip when <30m (good GPS), num when 30-100m,
        // "LOW" when >100m. The legacy accTag pasted the raw number always.
        if (snap.accuracy !== null) {
            const accM = Math.round(snap.accuracy);
            if (accM > 100) {
                parts.push(`  <acc>LOW</acc>`);
            }
            else if (accM >= 30) {
                parts.push(`  <acc m="${accM}"/>`);
            }
        }
        if (headingReliable) {
            parts.push(`  <heading_reliable/>`);
        }
        // <session> block — only when changed since last send (or first).
        // Note: client_id is NOT emitted here. It now sits as a top-level
        // attribute on <gps_update> so the bot sees it on every single
        // tick, not only when the session block fires. Avoids "which
        // client is this tick from" guesswork on every CRUISING update.
        if (sessionChanged) {
            const sessionLines = ['  <session>'];
            if (currentSnapshot.trackId !== null) {
                sessionLines.push(`    <artrack id="${currentSnapshot.trackId}"/>`);
            }
            sessionLines.push(`    <persona>${xmlEscape(currentSnapshot.persona)}</persona>`);
            // One deduped <focus> list: rotated intent FIRST (primary lens of
            // this tick), then the full chip selection, then any remaining
            // round-robin intents. Pre-fix only ONE of these surfaced per
            // tick and on tracks even that was partially suppressed — the
            // bot never saw the user's complete selection.
            const focusList = [];
            const pushFocus = (v) => {
                const t = v.trim();
                if (t && !focusList.includes(t))
                    focusList.push(t);
            };
            if (currentSnapshot.intent)
                pushFocus(currentSnapshot.intent);
            for (const slug of currentSnapshot.interests.split(','))
                pushFocus(slug);
            for (const it of this._userIntents)
                pushFocus(it);
            if (focusList.length === 0 && currentSnapshot.theme)
                pushFocus(currentSnapshot.theme);
            for (const f of focusList) {
                sessionLines.push(`    <focus>${xmlEscape(f)}</focus>`);
            }
            if (currentSnapshot.age !== null) {
                sessionLines.push(`    <age>${currentSnapshot.age}</age>`);
            }
            sessionLines.push(`    <lang>${xmlEscape(currentSnapshot.lang)}</lang>`);
            // deep_dive (Content #1213/#1214): session-level guide_mode + EXACT depth.
            // GuideDevBot's contract: these live in <session>. The agent then returns
            // additional_topics as progressively deeper layers of the SAME topic.
            // Additive: a persona without deep_dive simply ignores them.
            if (this._deepDiveDepth > 0) {
                sessionLines.push(`    <guide_mode>deep_dive</guide_mode>`);
                sessionLines.push(`    <depth>${this._deepDiveDepth}</depth>`);
            }
            sessionLines.push('  </session>');
            parts.push(...sessionLines);
        }
        // Flags — temporary signals, sent only when set this tick.
        const flagsParts = [];
        if (songReady)
            flagsParts.push('    <song_ready/>');
        if (isDriftRequest)
            flagsParts.push('    <drift_request/>');
        if (flagsParts.length > 0) {
            parts.push('  <flags>');
            parts.push(...flagsParts);
            parts.push('  </flags>');
        }
        // Memory — heard topics. Delta-aware: emit only NEW items that the
        // bot hasn't been told about yet. Without the delta the same heard
        // list re-arrives every 60s and the bot keeps saying "ich habe das
        // schon erzählt" for items the user is hearing for the first time.
        //
        // Drift-handling: items that DROP from the heard cache (24h decay,
        // see Wanderlaut HEARD-Mechanismus #808) are also pruned from the
        // snapshot so they can be re-emitted later if they reappear.
        let heardNewItems = [];
        let heardCurrentSet = new Set();
        if (this._heardCache?.tag) {
            const heardClean = this._heardCache.tag.replace(/^\s*/, '');
            const currentItems = heardClean
                .split(',')
                .map(s => s.trim())
                .filter(Boolean);
            heardCurrentSet = new Set(currentItems);
            // Prune snapshot to intersection with current cache.
            this._heardSnapshot = new Set(Array.from(this._heardSnapshot).filter(it => heardCurrentSet.has(it)));
            heardNewItems = currentItems.filter(it => !this._heardSnapshot.has(it));
        }
        else if (this._heardSnapshot.size > 0) {
            // Cache fully cleared since last tick — reset snapshot so a
            // re-population emits fresh.
            this._heardSnapshot = new Set();
        }
        // Compound <heard> items: each "Title|kid|km" so the bot has
        // knowledge_id + km-at-delivery for distance-based repeat-blocking.
        // Legacy items from _heardCache (global device-history, title-only)
        // emit bare. New items from _deliveredLog (this session) include
        // their pipe-delimited triple. Bot parses by splitting on comma
        // then pipe; absent pipes → title-only, backward-compatible.
        const deliveredItems = this._deliveredLog.map(d => {
            const title = d.title.replace(/[|,]/g, ' ').trim();
            const kid = d.kid ?? '';
            const km = d.km !== null ? d.km.toFixed(2) : '';
            return `${title}|${kid}|${km}`;
        });
        const allItems = [...heardNewItems, ...deliveredItems];
        if (allItems.length > 0) {
            parts.push('  <memory>');
            parts.push(`    <heard>${xmlEscape(allItems.join(','))}</heard>`);
            parts.push('  </memory>');
        }
        // <places> Auswahl-Liste removed (2026-06-08): real-device test
        // showed 16 of 20 entries were business noise (Frisierstube, Marketing
        // GmbH, etc.) that the bot ignored. Place info is now folded into
        // <location> at the top (single venue name when GPS is inside it,
        // street+suburb otherwise). Phase 2 will emit a Mapbox static image
        // here instead, letting the bot reason about spatial context via
        // vision rather than parsing OSM strings.
        parts.push('</gps_update>');
        const text = parts.join('\n');
        // Commit the sticky-snapshots AFTER successful build so a retry
        // with the same values doesn't re-trigger the blocks.
        this._sessionSnapshot = currentSnapshot;
        if (locationDesc) {
            this._locationSnapshot = locationDesc;
        }
        // Add NEW items to heard snapshot. heardCurrentSet was already
        // intersected above to drop expired items; we union the new ones in.
        for (const it of heardNewItems)
            this._heardSnapshot.add(it);
        // Capture the generation before any async work. If stop() is called
        // while we're awaiting, _stopGeneration increments and we discard
        // the response instead of playing stale TTS in a background tab.
        const gen = this._stopGeneration;
        try {
            // Pass lat/lon/track/persona to the enricher path. IacpClient
            // routes to guide-api /api/v1/live-send when both these params
            // and an enricherBase are set; otherwise falls through to the
            // legacy direct-to-cloud-api path. Either way the call shape
            // here is unchanged.
            const msgId = await this._iacp.sendMessage(text, 'gps-tracker', {
                lat, lon,
                trackId: this._specialTrack.isActive ? this._specialTrack.trackId : null,
                persona: this._narrator,
            });
            if (gen !== this._stopGeneration)
                return; // stopped while sending
            this._tracking.incrementSent();
            this._logger.info(`→ Sent #${this._tracking.sentCount} (msgId=${msgId})`);
            this._remoteLog(`→ Sent #${this._tracking.sentCount} (msgId=${msgId}) to session=${this._config.session}`, 'iacp');
            await this._iacp.pollResponse(msgId);
            if (gen !== this._stopGeneration)
                return; // stopped while polling
        }
        catch (err) {
            if (gen !== this._stopGeneration)
                return; // stopped, discard error
            const msg = err instanceof Error ? err.message : String(err);
            this._logger.error(`Send error: ${msg}`);
            this._remoteLog(`Send error: ${msg}`, 'iacp', 'Error');
            this._bus.emit('audio-guide:error', err);
        }
    }
    /**
     * Fetch Wikipedia image + extract for a topic and merge into
     * the TopicCardState. Uses the wikipedia URL or title as lookup key.
     */
    async _enrichTopicFromWikipedia(topic) {
        // Card identity at enrichment START — every merge below passes it so a
        // slow lookup that resolves after the user moved to the NEXT card is
        // dropped instead of stamping the previous card's image onto it.
        const epoch = this._topicCard.epoch;
        this._topicCard.setEnriching(true);
        // Extract the article title from the wikipedia URL if available
        let lookupTitle = topic.title;
        if (topic.wikipedia) {
            const wikiMatch = topic.wikipedia.match(/\/wiki\/([^#?]+)/);
            if (wikiMatch) {
                lookupTitle = decodeURIComponent(wikiMatch[1].replace(/_/g, ' '));
            }
        }
        // Image search query: prefer the bot-authored image_search_hint over
        // the topic title. For abstract topics like "Neuer Platz" the bot
        // sets hint="Lindwurmbrunnen Klagenfurt" which finds the right
        // landmark photo, while "Neuer Platz" alone is too ambiguous and
        // returns random side-buildings on the square. Falls back to
        // image_search then title for older bot responses.
        const imageQuery = topic.image_search_hint?.trim() ||
            topic.image_search?.trim() ||
            topic.title;
        if (imageQuery !== topic.title) {
            this._remoteLog(`Image query overridden by hint: "${imageQuery}" (topic "${topic.title}")`, 'wiki');
        }
        // PRIORITY 0: check cache for a reviewed image. Look up by hint
        // when set — otherwise we miss cached entries that were saved under
        // the concrete motif name.
        if (this._findImageCallback) {
            try {
                const snap = this._location.snapshot();
                const cachedImage = await this._findImageCallback(imageQuery, snap.lat ?? undefined, snap.lon ?? undefined);
                if (cachedImage && AudioGuide._isImageUrlTrusted(cachedImage)) {
                    this._remoteLog(`Cache hit: image for "${imageQuery}" from history`, 'wiki');
                    this._topicCard.mergeEnrichment({
                        description: topic.description,
                        image: cachedImage,
                        wikipedia: topic.wikipedia,
                        imageSize: 'hero',
                    }, epoch);
                    this._shownImages.add(cachedImage);
                    return; // Skip Wikipedia/Places — we have a verified cache hit
                }
                else if (cachedImage) {
                    this._remoteLog(`Cache image blocked by domain filter: ${cachedImage}`, 'wiki', 'Warning');
                }
            }
            catch { /* fall through to Wikipedia */ }
        }
        this._remoteLog(`Wiki enriching: "${lookupTitle}" (from topic "${topic.title}")`, 'wiki');
        try {
            const snap = this._location.snapshot();
            const localLang = wikiLangForCountry(snap.countryCode);
            const summary = await this._wikipedia.enrich(lookupTitle, 'de', snap.lat ?? undefined, snap.lon ?? undefined, snap.city ?? undefined, localLang, typeof topic.lat === 'number' ? topic.lat : undefined, typeof topic.lon === 'number' ? topic.lon : undefined);
            // ROCK-SOLID POSITION: the resolved article carries the REAL coordinate
            // of the very entity we're showing/telling about. Move the map marker
            // there — the bot's guessed topic.lat/lon put „Orahovica Festung" in the
            // lake; the resolved „Ružica grad" coordinate is the truth. Epoch-guarded
            // so a slow lookup for a past topic never yanks a newer topic's marker,
            // and radius-sanity so a mis-resolution can't teleport the pin far away.
            if (summary &&
                typeof summary.lat === 'number' &&
                typeof summary.lon === 'number' &&
                this._mapRenderer &&
                epoch === this._topicCard.epoch) {
                const drift = AudioGuide._haversineMeters(snap.lat ?? 0, snap.lon ?? 0, summary.lat, summary.lon);
                if (drift <= AudioGuide.MAX_FOCUS_DISTANCE_KM * 1000) {
                    this._mapRenderer.focusOnLocation(summary.lat, summary.lon, topic.title);
                    this._remoteLog(`Focus corrected to verified „${summary.title}" coord (${Math.round(drift)}m from user)`, 'geocode');
                }
            }
            if (summary && summary.image && summary.imageIsPhoto) {
                // Good photo — show as hero (fullscreen) or thumbnail depending on resolution
                this._remoteLog(`Wiki enriched: "${summary.title}" img=${summary.imageSize} (${summary.imageWidth}x${summary.imageHeight}) extract=${summary.extract.length}chars`, 'wiki');
                let enrichImage = summary.imageSize === 'icon' ? undefined : summary.image;
                const alreadyShown = enrichImage ? this._shownImages.has(enrichImage) : false;
                if (enrichImage)
                    this._shownImages.add(enrichImage);
                this._topicCard.mergeEnrichment({
                    description: summary.extract || topic.description,
                    image: enrichImage,
                    wikipedia: summary.url || topic.wikipedia,
                    // Already shown → thumbnail only (no fullscreen Ken Burns), otherwise hero
                    imageSize: alreadyShown ? 'thumbnail' : summary.imageSize,
                }, epoch);
            }
            else {
                // No image, or image is a map/diagram/coat of arms — skip it.
                const reason = summary?.image
                    ? 'filtered (map/diagram/svg)'
                    : summary ? 'no image' : 'not found';
                this._remoteLog(`Wiki ${reason} for "${lookupTitle}" — trying Google Places photo`, 'wiki', 'Warning');
                const wikiDesc = summary?.extract || null;
                // Wikipedia URL: only use if article exists (summary is non-null).
                // If summary is null, the bot's wikipedia URL is unvalidated → drop it.
                const wikipediaUrl = summary?.url || (summary ? topic.wikipedia : undefined);
                // Try fast live search (Commons + DDG) before Places fallback.
                // Both queries use imageQuery (the bot's image_search_hint if
                // present) so abstract topic titles don't drag down precision.
                let foundImage = null;
                if (this._findImageFastCallback) {
                    try {
                        const snapCity = this._location.snapshot().city ?? '';
                        foundImage = await this._findImageFastCallback(imageQuery, snapCity);
                        if (foundImage) {
                            this._remoteLog(`Fast image found for "${imageQuery}"`, 'wiki');
                        }
                    }
                    catch { /* fall through to Places */ }
                }
                if (foundImage && this._shownImages.has(foundImage)) {
                    foundImage = null;
                }
                if (foundImage && !AudioGuide._isImageUrlTrusted(foundImage)) {
                    this._remoteLog(`Fast image blocked by domain filter: ${foundImage}`, 'wiki', 'Warning');
                    foundImage = null;
                }
                if (!foundImage) {
                    foundImage = await this._fetchPlacesPhoto(imageQuery, topic.lat, topic.lon);
                    if (foundImage && this._shownImages.has(foundImage)) {
                        foundImage = null;
                    }
                    if (foundImage && !AudioGuide._isImageUrlTrusted(foundImage)) {
                        this._remoteLog(`Places image blocked by domain filter: ${foundImage}`, 'wiki', 'Warning');
                        foundImage = null;
                    }
                }
                if (foundImage)
                    this._shownImages.add(foundImage);
                this._topicCard.mergeEnrichment({
                    description: wikiDesc || topic.description,
                    image: foundImage || undefined,
                    wikipedia: wikipediaUrl,
                }, epoch);
            }
        }
        catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            this._remoteLog(`Wiki enrichment ERROR: ${msg}`, 'wiki', 'Error');
            this._topicCard.setEnriching(false);
        }
    }
    /**
     * Fetch a photo URL from Google Places for a topic name + location.
     * Uses the artrack /places/nearby endpoint to find the place, then
     * resolves the photo_reference to a usable image URL.
     */
    async _fetchPlacesPhoto(name, lat, lon) {
        if (!lat || !lon)
            return null;
        const apiBase = this._config.osmApiBase || 'https://api-artrack.arkturian.com';
        try {
            const res = await fetch(`${apiBase}/places/nearby?lat=${lat}&lng=${lon}&radius_m=200`, { headers: { 'X-API-KEY': 'Inetpass1' } });
            if (!res.ok)
                return null;
            const data = await res.json();
            // Find the place that MATCHES the topic name — no random fallback
            const features = data.features || [];
            const match = features.find((f) => f.photo_ref && (f.name.toLowerCase().includes(name.toLowerCase().slice(0, 10))
                || name.toLowerCase().includes(f.name.toLowerCase().slice(0, 10))));
            if (match?.photo_ref) {
                // Google Places Photo API URL — always 302 to a CDN. fetch HEAD
                // with redirect:follow fails on the CDN hop because Google's
                // image CDN doesn't return CORS headers for arbitrary origins.
                // Use Image() loading instead — img elements follow redirects
                // transparently and naturalWidth/Height are CORS-exempt for
                // pixel-free metadata. Also lets us reject tiny placeholder
                // responses (Google sometimes serves 1x1 errors).
                const photoUrl = `https://maps.googleapis.com/maps/api/place/photo?maxwidth=800&photo_reference=${match.photo_ref}&key=AIzaSyDAZnfJ30Hrs0LmBpNrGdzBvs9DV3_82BY`;
                const valid = await new Promise((resolve) => {
                    const img = new Image();
                    const timer = window.setTimeout(() => resolve(false), 5000);
                    img.onload = () => {
                        window.clearTimeout(timer);
                        resolve(img.naturalWidth >= 100 && img.naturalHeight >= 100);
                    };
                    img.onerror = () => {
                        window.clearTimeout(timer);
                        resolve(false);
                    };
                    img.src = photoUrl;
                });
                if (!valid) {
                    this._remoteLog(`Places photo invalid for "${name}" (load failed or below 100x100)`, 'wiki', 'Warning');
                    return null;
                }
                this._remoteLog(`Places photo found for "${name}" via "${match.name}"`, 'wiki');
                return photoUrl;
            }
            this._remoteLog(`No Places photo for "${name}" (${features.length} features, none with photo)`, 'wiki', 'Warning');
            return null;
        }
        catch {
            return null;
        }
    }
    async _sendVoiceMessage(transcript) {
        if (!this._location.hasPosition)
            return;
        // Clear the playback pipeline so the voice response activates immediately
        // when it arrives. Without this, the previous active topic stays in
        // _activePackage even after pauseSpeech() (because pauseSpeech only
        // stops the audio source, doesn't clear the slot). The incoming voice
        // response would then queue into _pendingPackage and wait for a
        // tts:end event that never fires — answer hangs in limbo.
        this._activePackage = null;
        this._pendingPackage = null;
        this._additionalQueue = [];
        this._pendingAdditionals = [];
        this._remoteLog(`→ Voice msg sending: "${transcript.slice(0, 60)}${transcript.length > 60 ? '…' : ''}"`, 'voice');
        const snap = this._location.snapshot();
        const lat = snap.lat;
        const lon = snap.lon;
        const heading = snap.effectiveHeading;
        const dir = heading !== null ? AudioGuide._bearingName(heading) : '?';
        const street = snap.street || '-';
        const trackTag = this._specialTrack.isActive
            ? ` [TRACK:${this._specialTrack.trackId}]`
            : '';
        const text = '[Voice Message] ' +
            transcript +
            ' [Position: ' +
            lat.toFixed(5) +
            ',' +
            lon.toFixed(5) +
            ' | ' +
            street +
            ' | Richtung: ' +
            dir +
            ']' +
            trackTag;
        try {
            const msgId = await this._iacp.sendMessage(text, 'voice');
            this._logger.info(`→ Voice msg sent (msgId=${msgId}): "${transcript.slice(0, 40)}${transcript.length > 40 ? '…' : ''}"`);
            this._remoteLog(`→ Voice sent msgId=${msgId} session=${this._config.session}`, 'voice');
            await this._iacp.pollResponse(msgId);
            this._remoteLog(`✓ Voice response received msgId=${msgId}`, 'voice');
        }
        catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            this._logger.error(`Voice send error: ${msg}`);
            this._remoteLog(`✗ Voice send error: ${msg}`, 'voice', 'Error');
            this._bus.emit('audio-guide:error', err);
        }
    }
    // ──────────────────────────────────────────────────────────────
    // Remote Logging (fire-and-forget, never blocks)
    // ──────────────────────────────────────────────────────────────
    _initRemoteLog() {
        if (this._logSessionCreated)
            return;
        this._logSessionCreated = true;
        fetch(`${AudioGuide.LOG_API}/sessions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                id: AudioGuide.LOG_SESSION,
                client_id: 'audio-guide',
                platform: 'web',
            }),
        }).catch(() => { });
    }
    _remoteLog(message, source, type = 'Log') {
        fetch(`${AudioGuide.LOG_API}/sessions/${AudioGuide.LOG_SESSION}/logs`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                entries: [{ type, message, source }],
            }),
        }).catch(() => { });
    }
    // ──────────────────────────────────────────────────────────────
    // Static Helpers
    // ──────────────────────────────────────────────────────────────
    static _bearingName(deg) {
        const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
        return dirs[Math.round(((deg % 360) + 360) % 360 / 45) % 8];
    }
    /**
     * Client-side defence-in-depth for image domains. Matches the
     * backend's DDG blacklist so even if a bad URL leaks through the
     * server, the frontend refuses to show it. Returns false → caller
     * drops the image and shows the topic card without a hero.
     *
     * This is the "Pre-Display-Gate" without the cost of a vision call:
     * we accept that a few good images will get filtered (false negatives
     * are cheap — no image is better than a wrong image), and reject
     * everything that looks like stock / autogen / foreign-language
     * tourism content.
     */
    static _isImageUrlTrusted(url) {
        const u = url.toLowerCase();
        const blockedSubstrings = [
            // Stock photo aggregators
            'pinimg.com', 'alamy.com', 'shutterstock.com', 'gettyimages',
            'istockphoto', 'depositphotos', 'dreamstime', '123rf.com',
            'stock.adobe.com', 'freepik.com', 'vecteezy.com',
            // Video / autogen thumbnails
            'ytimg.com', 'ggpht.com', 'vimeocdn.com',
            // Pinterest / aggregator mirrors
            'pinterest.', 'tumblr.com',
            // Foreign tourism portals seen polluting AT/DE results
            'karyntia.pl',
            // TLDs unlikely to host accurate AT/DE landmarks
            '.ir/', '.ru/', '.by/', '.cn/', '.tr/',
        ];
        return !blockedSubstrings.some(b => u.includes(b));
    }
    static _haversineMeters(lat1, lon1, lat2, lon2) {
        const R = 6371000;
        const toRad = (d) => (d * Math.PI) / 180;
        const dLat = toRad(lat2 - lat1);
        const dLon = toRad(lon2 - lon1);
        const a = Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }
    /** Compress a photo to JPEG with a max long-edge dimension.
     *  Uses createImageBitmap (fast, native) when available, falls back to
     *  HTMLImageElement otherwise. Returns the original blob if anything
     *  goes wrong so we never block uploads on compression failures. */
    static async _compressPhoto(blob, maxDim, quality) {
        // Fast path — modern browsers
        let bitmap = null;
        try {
            bitmap = await createImageBitmap(blob);
        }
        catch { /* fall through */ }
        let width, height;
        let drawSource;
        if (bitmap) {
            width = bitmap.width;
            height = bitmap.height;
            drawSource = bitmap;
        }
        else {
            // Fallback — Safari sometimes refuses createImageBitmap on HEIC
            const url = URL.createObjectURL(blob);
            try {
                const img = await new Promise((resolve, reject) => {
                    const i = new Image();
                    i.onload = () => resolve(i);
                    i.onerror = () => reject(new Error('image decode failed'));
                    i.src = url;
                });
                width = img.naturalWidth;
                height = img.naturalHeight;
                drawSource = img;
            }
            finally {
                URL.revokeObjectURL(url);
            }
        }
        if (width === 0 || height === 0)
            return blob;
        // Already small enough → just re-encode to JPEG if needed
        const longEdge = Math.max(width, height);
        let scale = 1;
        if (longEdge > maxDim)
            scale = maxDim / longEdge;
        const outW = Math.round(width * scale);
        const outH = Math.round(height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = outW;
        canvas.height = outH;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return blob;
        ctx.drawImage(drawSource, 0, 0, outW, outH);
        if (bitmap)
            bitmap.close();
        const out = await new Promise(resolve => {
            canvas.toBlob(b => resolve(b), 'image/jpeg', quality);
        });
        return out && out.size > 0 ? out : blob;
    }
    /**
     * Parse the nearest POI from the osmTag prefetch string.
     * Format: " [PLACES:200m,N] Name (kind, 35m) | Name2 (kind, 41m) | ..."
     * Returns null if the tag isn't a PLACES entry or the first item
     * fails to parse.
     */
    _parseNearestPoiFromOsm(osmTag) {
        const wrapperMatch = osmTag.match(/^\s*\[PLACES:\d+m,\d+\]\s*(.+)$/);
        if (!wrapperMatch)
            return null;
        const firstItem = wrapperMatch[1].split('|')[0]?.trim();
        if (!firstItem)
            return null;
        const m = firstItem.match(/^(.*?)\s*\(([^,]+),\s*(\d+)m(?:[^)]*)?\)\s*$/);
        if (!m)
            return null;
        return {
            name: m[1].trim(),
            kind: m[2].trim(),
            dist: parseInt(m[3], 10),
        };
    }
    /**
     * Build a single human-readable location description.
     * Returns ONE clear statement of where the user is, never a choice list.
     *
     * Decision:
     *   1. Nearest POI within 15m AND kind is a public venue
     *        → use venue name as primary ("Strandbad Loretto")
     *   2. Else if street known + suburb known
     *        → "<street> in <suburb>"
     *   3. Else street only or suburb only
     *
     * Then append: city, region (country dropped as redundant when
     * region is present).
     *
     * Examples:
     *   "Strandbad Loretto, Klagenfurt am Wörthersee, Kärnten"
     *   "Heimstättenstraße in Waidmannsdorf, Klagenfurt am Wörthersee, Kärnten"
     *   "Tscheppaschlucht, Kärnten"
     */
    _buildLocationDescription(snap, osmTag) {
        let primary = null;
        const nearestPoi = this._parseNearestPoiFromOsm(osmTag);
        if (nearestPoi
            && nearestPoi.dist < AudioGuide._VENUE_THRESHOLD_M
            && AudioGuide._VENUE_KINDS.has(nearestPoi.kind)) {
            primary = nearestPoi.name;
        }
        else if (snap.street && snap.street !== '-' && snap.suburb) {
            primary = `${snap.street} in ${snap.suburb}`;
        }
        else if (snap.street && snap.street !== '-') {
            primary = snap.street;
        }
        else if (snap.suburb) {
            primary = snap.suburb;
        }
        const parts = [];
        if (primary)
            parts.push(primary);
        if (snap.city)
            parts.push(snap.city);
        if (snap.region)
            parts.push(snap.region);
        return parts.length > 0 ? parts.join(', ') : null;
    }
}
AudioGuide.MAX_DELIVERED_ENTRIES = 20;
/** Breather in ms between back-to-back TTS playbacks from the queue.
 *  Long enough that the music-ducker un-ducks and the user briefly hears
 *  the background track at full volume — feels like a natural beat
 *  between two stories instead of a hard cut. */
AudioGuide.QUEUE_PAUSE_MS = 4000;
AudioGuide.SONG_RHYTHM_MS = 8 * 60 * 1000;
AudioGuide.GEOCODE_THROTTLE_MS = 15 * 1000;
AudioGuide.MAX_FOCUS_DISTANCE_KM = 500;
AudioGuide.LOG_API = 'https://log-api.arkturian.com/api/v1';
AudioGuide.LOG_SESSION = 'audio-guide';
/**
 * POI kinds that map to a "I am AT this place" semantic — open public
 * venues where standing GPS-inside is a meaningful statement (you're
 * at the swimming bath, in the museum, on the viewpoint). Excludes
 * lodging (Airbnb), private businesses (Frisierstube), generic
 * point_of_interest, retail, services. Curated against OSM keys +
 * Google Places types observed in the artrack feed.
 */
AudioGuide._VENUE_KINDS = new Set([
    // Bathing / water
    'public_bath', 'swimming_pool', 'beach', 'marina',
    // Nature
    'park', 'garden', 'nature_reserve', 'viewpoint',
    // F&B (only if user is GPS-inside, threshold 15m)
    'cafe', 'restaurant', 'pub', 'bar', 'biergarten',
    // Culture
    'museum', 'gallery', 'theater', 'library', 'cinema',
    // Worship
    'church', 'place_of_worship', 'chapel',
    // Heritage
    'castle', 'monument', 'memorial', 'ruins',
    // Tourism
    'attraction', 'tourist_attraction',
    // Sports
    'stadium', 'sports_centre',
]);
/** Distance in meters under which a nearest venue is treated as
 *  "the user is AT this venue" instead of "somewhere on a street". */
AudioGuide._VENUE_THRESHOLD_M = 15;
//# sourceMappingURL=AudioGuide.js.map